/**
 * Blueprint Admin Page - Command Center PRO
 * ==========================================
 * Features:
 * - Collapsible sidebar with burger menu
 * - Image upload with crop editor
 * - Icon selector for cards
 * - Full CMS functionality
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useBlueprint } from '../context/BlueprintContext';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import BlueprintPagesManager from '../components/blueprint/BlueprintPagesManager';
import SidebarConfigEditor from '../components/blueprint/SidebarConfigEditor';
import UserMenuConfigEditor from '../components/blueprint/UserMenuConfigEditor';
import HomepageEditor from '../components/blueprint/HomepageEditor';
import AdminSupportPanel from './admin/AdminSupportPanel';
import AdminProfessionalsConfig from './admin/AdminProfessionalsConfig';
import AdminProfessionalsManagement from './admin/AdminProfessionalsManagement';
import { 
  LayoutDashboard, Database, Languages, Hexagon, Zap, Settings, 
  Activity, LogOut, ExternalLink, Globe, Users, Menu, X,
  FileText, Check, Save, CreditCard, Loader2, RefreshCw, 
  Upload, Home, Image, BarChart3, MousePointer, Sun, Crop,
  Grid3X3, Search, Layers, FolderOpen, Eye, Sparkles, Box,
  Palette, Target, Compass, Star, Heart, Bookmark, Tag,
  Shield, Award, Briefcase, Camera, Film, Music, MapPin, Plus,
  Clock, AlertCircle, MessageCircle, HelpCircle, Building2,
  Newspaper, Trash2, Edit, ChevronRight, Calendar, User, Link
} from 'lucide-react';

// ============================================================================
// ICON LIBRARY - Available icons for cards
// ============================================================================

const ICON_LIBRARY = [
  { id: 'grid', icon: Grid3X3, label: 'Grid' },
  { id: 'search', icon: Search, label: 'Search' },
  { id: 'layers', icon: Layers, label: 'Layers' },
  { id: 'folder', icon: FolderOpen, label: 'Folder' },
  { id: 'eye', icon: Eye, label: 'Eye' },
  { id: 'sparkles', icon: Sparkles, label: 'Sparkles' },
  { id: 'box', icon: Box, label: 'Box' },
  { id: 'palette', icon: Palette, label: 'Palette' },
  { id: 'target', icon: Target, label: 'Target' },
  { id: 'compass', icon: Compass, label: 'Compass' },
  { id: 'star', icon: Star, label: 'Star' },
  { id: 'heart', icon: Heart, label: 'Heart' },
  { id: 'bookmark', icon: Bookmark, label: 'Bookmark' },
  { id: 'tag', icon: Tag, label: 'Tag' },
  { id: 'shield', icon: Shield, label: 'Shield' },
  { id: 'award', icon: Award, label: 'Award' },
  { id: 'briefcase', icon: Briefcase, label: 'Briefcase' },
  { id: 'camera', icon: Camera, label: 'Camera' },
  { id: 'film', icon: Film, label: 'Film' },
  { id: 'music', icon: Music, label: 'Music' },
  { id: 'map', icon: MapPin, label: 'Map' },
  { id: 'zap', icon: Zap, label: 'Zap' },
  { id: 'database', icon: Database, label: 'Database' },
  { id: 'settings', icon: Settings, label: 'Settings' }
];

// ============================================================================
// SIDEBAR CONFIG
// ============================================================================

const SIDEBAR_ITEMS = [
  { id: 'overview', icon: LayoutDashboard, label: 'OVERVIEW' },
  { id: 'dashboard-config', icon: Layers, label: 'DASHBOARD' },
  { id: 'sidebar-config', icon: Menu, label: 'SIDEBAR MENU' },
  { id: 'user-menu-config', icon: User, label: 'USER MENU' },
  { id: 'project-templates', icon: Briefcase, label: 'TEMPLATES' },
  { id: 'homepage', icon: Home, label: 'HOMEPAGE' },
  { id: 'pages', icon: FileText, label: 'PAGES' },
  { id: 'posts', icon: Newspaper, label: 'POSTS' },
  { id: 'professionals', icon: Building2, label: 'PROFESSIONISTI' },
  { id: 'dna-compare', icon: BarChart3, label: 'DNA COMPARE' },
  { id: 'support', icon: MessageCircle, label: 'SUPPORTO' },
  { id: 'users', icon: Users, label: 'USERS' },
  { id: 'plans', icon: CreditCard, label: 'PLANS' },
  { id: 'materials', icon: Database, label: 'MATERIALS' },
  { id: 'translations', icon: Languages, label: 'TRANSLATIONS' },
  { id: 'dna-matrix', icon: Hexagon, label: 'DNA MATRIX' },
  { id: 'sre-engine', icon: Zap, label: 'SRE ENGINE' },
  { id: 'logs', icon: Activity, label: 'SYSTEM LOGS' }
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BlueprintAdminPage = () => {
  const navigate = useNavigate();
  const { refreshConfig } = useBlueprint();
  const { user, profile, logout, loading: authLoading, isAdmin, authInitialized } = useAuth();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Data states
  const [configs, setConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ materials: 0, users: 0, drafts: 0 });
  
  // Homepage editor state
  const [homeTab, setHomeTab] = useState('hero');
  const [heroData, setHeroData] = useState({});
  const [discoveryData, setDiscoveryData] = useState({ cards: [] });
  const [statsData, setStatsData] = useState([]);
  const [ctaData, setCtaData] = useState({});
  
  // Dashboard config editor state
  const [dashboardConfigs, setDashboardConfigs] = useState([]);
  const [selectedDashboardConfig, setSelectedDashboardConfig] = useState(null);
  const [dashboardTab, setDashboardTab] = useState('hero');
  const [dashboardEditData, setDashboardEditData] = useState(null);
  
  // Project templates state
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateEditData, setTemplateEditData] = useState(null);
  
  // DNA Status state
  const [dnaStatus, setDnaStatus] = useState(null);
  const [dnaLoading, setDnaLoading] = useState(false);
  const [recentDna, setRecentDna] = useState([]);
  
  // DNA Compare CMS state
  const [dnaCompareConfig, setDnaCompareConfig] = useState(null);
  const [dnaCompareLoading, setDnaCompareLoading] = useState(false);

  // ============================================================================
  // AUTH PROTECTION - Redirect to login if not authenticated or not admin
  // ============================================================================
  
  useEffect(() => {
    // Wait for auth to FULLY initialize (not just loading)
    if (authLoading || !authInitialized) {
      console.log('🔒 Blueprint: Auth not ready yet', { authLoading, authInitialized });
      return;
    }
    
    // No user = redirect to login
    if (!user) {
      console.log('🔒 Blueprint: No user session, redirecting to login');
      navigate('/login', { replace: true, state: { from: '/blueprint' } });
      return;
    }
    
    // If profile is still loading (null), wait for it
    if (!profile) {
      console.log('🔒 Blueprint: Profile not loaded yet, waiting...');
      return;
    }
    
    // User exists but not admin = redirect to dashboard
    if (!isAdmin) {
      console.log('🔒 Blueprint: User is not admin, redirecting to dashboard');
      console.log('🔒 Blueprint: profile.role_system =', profile?.role_system);
      navigate('/dashboard', { replace: true });
      return;
    }
    
    // All good - user is admin
    console.log('🔒 Blueprint: Admin verified, access granted');
  }, [user, profile, authLoading, authInitialized, isAdmin, navigate]);

  // ============================================================================
  // TOAST
  // ============================================================================
  
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadData = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    
    try {
      const { data: configData } = await supabase
        .from('blueprint_config')
        .select('*')
        .eq('lang', 'it')
        .order('key');
      
      setConfigs(configData || []);
      
      if (configData) {
        const heroConfig = configData.find(c => c.key === 'ui.home.hero');
        const discoveryConfig = configData.find(c => c.key === 'ui.home.discovery');
        const statsConfig = configData.find(c => c.key === 'ui.home.stats');
        const ctaConfig = configData.find(c => c.key === 'ui.home.cta');
        
        if (heroConfig?.value_json) setHeroData(heroConfig.value_json);
        if (discoveryConfig?.value_json) setDiscoveryData(discoveryConfig.value_json);
        if (statsConfig?.value_json) setStatsData(statsConfig.value_json);
        if (ctaConfig?.value_json) setCtaData(ctaConfig.value_json);
      }
      
      const { count: matCount } = await supabase
        .from('materials')
        .select('*', { count: 'exact', head: true });
      
      // Fetch users from Supabase profiles table
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        setUsers([]);
      } else {
        console.log('Users loaded from Supabase:', profilesData?.length || 0);
        setUsers(profilesData || []);
      }
      
      const { data: planData } = await supabase
        .from('plans')
        .select('*')
        .order('price');
      
      setPlans(planData || []);
      
      // Stats will be updated by separate useEffect watching users
      setStats(prev => ({
        ...prev,
        materials: matCount || 0,
        drafts: 18
      }));
      
      // Load dashboard configs
      const { data: dashConfigs } = await supabase
        .from('blueprint_dashboard_config')
        .select('*')
        .order('role_professional');
      
      setDashboardConfigs(dashConfigs || []);
      
      // Load project templates
      const { data: templates } = await supabase
        .from('project_templates')
        .select('*')
        .order('role_professional');
      
      setProjectTemplates(templates || []);
      
    } catch (err) {
      console.error('Load error:', err);
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update stats when users change
  useEffect(() => {
    setStats(prev => ({
      ...prev,
      users: users.length
    }));
  }, [users]);

  // ============================================================================
  // SAVE FUNCTIONS
  // ============================================================================

  const saveConfig = async (key, value) => {
    if (!supabase || !user) {
      showToast('Devi essere autenticato per salvare', 'error');
      return false;
    }
    
    setSaving(true);
    console.log('=== SAVING CONFIG ===');
    console.log('Key:', key);
    console.log('Value:', JSON.stringify(value, null, 2));
    
    try {
      // Try update first
      const { data: updateData, error: updateError } = await supabase
        .from('blueprint_config')
        .update({ 
          value_json: value,
          updated_at: new Date().toISOString()
        })
        .eq('key', key)
        .eq('lang', 'it')
        .select();
      
      console.log('Update result:', { updateData, updateError });
      
      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }
      
      // If no rows updated, try insert
      if (!updateData || updateData.length === 0) {
        console.log('No rows updated, trying insert...');
        const { data: insertData, error: insertError } = await supabase
          .from('blueprint_config')
          .insert({
            key,
            lang: 'it',
            value_json: value,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();
        
        console.log('Insert result:', { insertData, insertError });
        
        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        
        if (!insertData || insertData.length === 0) {
          throw new Error('Nessun dato salvato - verifica le RLS policies');
        }
      }
      
      console.log('=== SAVE SUCCESS ===');
      showToast(`${key} salvato!`);
      await loadData();
      if (refreshConfig) await refreshConfig();
      setSaving(false);
      return true;
      
    } catch (err) {
      console.error('Save error:', err);
      showToast(`Errore: ${err.message}`, 'error');
      setSaving(false);
      return false;
    }
  };

  const updateUser = async (userId, updates) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);
      
      if (error) throw error;
      showToast('Utente aggiornato!');
      await loadData();
    } catch (err) {
      showToast(`Errore: ${err.message}`, 'error');
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🔒 Blueprint: Logging out...');
      if (logout) {
        await logout();
      }
      // Also clear Supabase session directly
      if (supabase) {
        await supabase.auth.signOut();
      }
      showToast('Logout effettuato');
      // Force reload to clear all state
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      showToast('Errore logout', 'error');
    }
  };

  // ============================================================================
  // RENDER: TOP STATS BAR
  // ============================================================================

  const TopStatsBar = () => (
    <div className="flex items-center gap-4 p-4 bg-[#1a1a18] rounded-lg border border-white/5 overflow-x-auto">
      <StatBadge icon={<div className="w-2 h-2 rounded-full bg-green-500" />} label="API" value="ONLINE" valueColor="text-green-400" />
      <div className="w-px h-10 bg-white/10 flex-shrink-0" />
      <StatBadge icon={<Zap size={14} className="text-[#D6C1A5]" />} label="SRE" value="ACTIVE" valueColor="text-[#D6C1A5]" />
      <div className="w-px h-10 bg-white/10 flex-shrink-0" />
      <StatBadge icon={<Database size={14} className="text-orange-400" />} label="MATERIALS" value={stats.materials.toString()} valueColor="text-orange-400" />
      <div className="w-px h-10 bg-white/10 flex-shrink-0" />
      <StatBadge icon={<Users size={14} className="text-purple-400" />} label="USERS" value={stats.users.toString()} valueColor="text-purple-400" />
      <div className="w-px h-10 bg-white/10 flex-shrink-0" />
      <StatBadge icon={<FileText size={14} className="text-yellow-400" />} label="CONFIGS" value={configs.length.toString()} valueColor="text-yellow-400" />
    </div>
  );

  // ============================================================================
  // RENDER SECTIONS
  // ============================================================================

  const renderOverview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
        <h3 className="text-sm font-medium text-white/70 mb-4">Quick Actions</h3>
        <div className="space-y-2">
          {[
            { label: 'Edit Homepage', icon: Home, section: 'homepage', color: 'text-[#D6C1A5]' },
            { label: 'Manage Users', icon: Users, section: 'users', color: 'text-purple-400' },
            { label: 'Configure Plans', icon: CreditCard, section: 'plans', color: 'text-green-400' },
            { label: 'View Materials', icon: Database, section: 'materials', color: 'text-orange-400' }
          ].map(item => (
            <button 
              key={item.section}
              onClick={() => setActiveSection(item.section)}
              className="w-full p-3 bg-[#2a2a28] hover:bg-[#3a3a38] rounded-lg flex items-center gap-3 transition-colors"
            >
              <item.icon size={18} className={item.color} />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
        <h3 className="text-sm font-medium text-white/70 mb-4">Blueprint Configs ({configs.length})</h3>
        <div className="space-y-2 max-h-64 overflow-auto">
          {configs.slice(0, 8).map(config => (
            <div key={config.id || config.key} className="p-2 bg-[#2a2a28] rounded flex items-center justify-between">
              <span className="text-xs text-white/70 font-mono truncate">{config.key}</span>
              <span className="text-[10px] text-white/40 flex-shrink-0 ml-2">
                {new Date(config.updated_at).toLocaleDateString('it')}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
        <h3 className="text-sm font-medium text-white/70 mb-4">Recent Activity</h3>
        <div className="space-y-3 text-xs">
          {[
            { icon: '📦', text: 'Material updated', time: '2 min ago' },
            { icon: '👤', text: 'New user registered', time: '15 min ago' },
            { icon: '🌍', text: 'Translations updated', time: '32 min ago' },
            { icon: '⚙️', text: 'Config modified', time: '1 hour ago' }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <span>{item.icon}</span>
              <span className="flex-1 text-white/70">{item.text}</span>
              <span className="text-white/40">{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // DASHBOARD CONFIG SECTION
  // ============================================================================
  
  const saveDashboardConfig = async (configId, updates) => {
    if (!supabase || !user) {
      showToast('Devi essere autenticato per salvare', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_dashboard_config')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', configId);
      
      if (error) throw error;
      
      showToast('Dashboard config salvata!');
      loadData(); // Reload all data
    } catch (err) {
      console.error('Save error:', err);
      showToast('Errore: ' + err.message, 'error');
    }
    setSaving(false);
  };
  
  const renderDashboardConfig = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Dashboard Configurations</h2>
        <span className="text-sm text-white/40">{dashboardConfigs.length} configs</span>
      </div>
      
      {/* Role selector */}
      <div className="flex gap-2 flex-wrap">
        {dashboardConfigs.map(config => (
          <button
            key={config.id}
            onClick={() => {
              setSelectedDashboardConfig(config);
              setDashboardEditData({ ...config });
              setDashboardTab('hero');
            }}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              selectedDashboardConfig?.id === config.id
                ? 'bg-[#b08b5c] text-black'
                : 'bg-[#1a1a18] text-white/60 hover:text-white border border-white/10'
            }`}
          >
            {config.role_professional}
            {!config.is_active && <span className="ml-2 text-xs opacity-50">(off)</span>}
          </button>
        ))}
      </div>
      
      {/* Editor */}
      {selectedDashboardConfig && dashboardEditData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - Tabs and Editor */}
          <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6">
            {/* Tabs */}
            <div className="flex gap-2 flex-wrap mb-6">
              {['hero', 'feature_pills', 'action_cards', 'checklist', 'widgets'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setDashboardTab(tab)}
                  className={`px-3 py-1.5 rounded text-xs transition-colors ${
                    dashboardTab === tab
                      ? 'bg-[#b08b5c] text-black'
                      : 'bg-[#2a2a28] text-white/60 hover:text-white'
                  }`}
                >
                  {tab.replace('_', ' ').toUpperCase()}
                </button>
              ))}
            </div>
            
            {/* Hero Editor */}
            {dashboardTab === 'hero' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70">Hero Section</h3>
                <div>
                  <label className="text-xs text-white/50 block mb-1">Title</label>
                  <input 
                    type="text" 
                    value={dashboardEditData.hero?.title || ''} 
                    onChange={e => setDashboardEditData({
                      ...dashboardEditData, 
                      hero: { ...dashboardEditData.hero, title: e.target.value }
                    })}
                    className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 block mb-1">Title Accent (italic)</label>
                  <input 
                    type="text" 
                    value={dashboardEditData.hero?.title_accent || ''} 
                    onChange={e => setDashboardEditData({
                      ...dashboardEditData, 
                      hero: { ...dashboardEditData.hero, title_accent: e.target.value }
                    })}
                    className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 block mb-1">Title Suffix</label>
                  <input 
                    type="text" 
                    value={dashboardEditData.hero?.title_suffix || ''} 
                    onChange={e => setDashboardEditData({
                      ...dashboardEditData, 
                      hero: { ...dashboardEditData.hero, title_suffix: e.target.value }
                    })}
                    className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 block mb-1">Subtitle</label>
                  <textarea 
                    value={dashboardEditData.hero?.subtitle || ''} 
                    onChange={e => setDashboardEditData({
                      ...dashboardEditData, 
                      hero: { ...dashboardEditData.hero, subtitle: e.target.value }
                    })}
                    rows={3}
                    className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-sm resize-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/50 block mb-1">Hero Image URL</label>
                  <input 
                    type="text" 
                    value={dashboardEditData.hero?.hero_image || ''} 
                    onChange={e => setDashboardEditData({
                      ...dashboardEditData, 
                      hero: { ...dashboardEditData.hero, hero_image: e.target.value }
                    })}
                    className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}
            
            {/* Feature Pills Editor */}
            {dashboardTab === 'feature_pills' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70">Feature Pills</h3>
                {(dashboardEditData.feature_pills || []).map((pill, idx) => (
                  <div key={idx} className="p-3 bg-[#2a2a28] rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/40">#{idx + 1}</span>
                      <input
                        type="text"
                        value={pill.icon || ''}
                        onChange={e => {
                          const newPills = [...dashboardEditData.feature_pills];
                          newPills[idx] = { ...newPills[idx], icon: e.target.value };
                          setDashboardEditData({ ...dashboardEditData, feature_pills: newPills });
                        }}
                        placeholder="Icon (e.g., Layers)"
                        className="flex-1 bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      value={pill.title || ''}
                      onChange={e => {
                        const newPills = [...dashboardEditData.feature_pills];
                        newPills[idx] = { ...newPills[idx], title: e.target.value };
                        setDashboardEditData({ ...dashboardEditData, feature_pills: newPills });
                      }}
                      placeholder="Title"
                      className="w-full bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                    />
                    <input
                      type="text"
                      value={pill.description || ''}
                      onChange={e => {
                        const newPills = [...dashboardEditData.feature_pills];
                        newPills[idx] = { ...newPills[idx], description: e.target.value };
                        setDashboardEditData({ ...dashboardEditData, feature_pills: newPills });
                      }}
                      placeholder="Description"
                      className="w-full bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                    />
                  </div>
                ))}
              </div>
            )}
            
            {/* Action Cards Editor */}
            {dashboardTab === 'action_cards' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70">Action Cards</h3>
                {(dashboardEditData.action_cards || []).map((card, idx) => (
                  <div key={idx} className="p-3 bg-[#2a2a28] rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/40">#{idx + 1}</span>
                      <input
                        type="text"
                        value={card.title || ''}
                        onChange={e => {
                          const newCards = [...dashboardEditData.action_cards];
                          newCards[idx] = { ...newCards[idx], title: e.target.value };
                          setDashboardEditData({ ...dashboardEditData, action_cards: newCards });
                        }}
                        placeholder="Title"
                        className="flex-1 bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      value={card.description || ''}
                      onChange={e => {
                        const newCards = [...dashboardEditData.action_cards];
                        newCards[idx] = { ...newCards[idx], description: e.target.value };
                        setDashboardEditData({ ...dashboardEditData, action_cards: newCards });
                      }}
                      placeholder="Description"
                      className="w-full bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={card.route || ''}
                        onChange={e => {
                          const newCards = [...dashboardEditData.action_cards];
                          newCards[idx] = { ...newCards[idx], route: e.target.value };
                          setDashboardEditData({ ...dashboardEditData, action_cards: newCards });
                        }}
                        placeholder="Route (e.g., /projects)"
                        className="bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        value={card.icon || ''}
                        onChange={e => {
                          const newCards = [...dashboardEditData.action_cards];
                          newCards[idx] = { ...newCards[idx], icon: e.target.value };
                          setDashboardEditData({ ...dashboardEditData, action_cards: newCards });
                        }}
                        placeholder="Icon"
                        className="bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Checklist Editor */}
            {dashboardTab === 'checklist' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70">Checklist Items</h3>
                {(dashboardEditData.checklist_items || []).map((item, idx) => (
                  <div key={idx} className="p-3 bg-[#2a2a28] rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/40">#{item.number || idx + 1}</span>
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={e => {
                          const newItems = [...dashboardEditData.checklist_items];
                          newItems[idx] = { ...newItems[idx], title: e.target.value };
                          setDashboardEditData({ ...dashboardEditData, checklist_items: newItems });
                        }}
                        placeholder="Title"
                        className="flex-1 bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      value={item.description || ''}
                      onChange={e => {
                        const newItems = [...dashboardEditData.checklist_items];
                        newItems[idx] = { ...newItems[idx], description: e.target.value };
                        setDashboardEditData({ ...dashboardEditData, checklist_items: newItems });
                      }}
                      placeholder="Description"
                      className="w-full bg-[#1a1a18] border border-white/10 rounded px-2 py-1 text-xs"
                    />
                  </div>
                ))}
              </div>
            )}
            
            {/* Widgets Editor (JSON) */}
            {dashboardTab === 'widgets' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-white/70">Widgets (JSON)</h3>
                <textarea
                  value={JSON.stringify(dashboardEditData.widgets || [], null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setDashboardEditData({ ...dashboardEditData, widgets: parsed });
                    } catch {}
                  }}
                  rows={15}
                  className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-xs font-mono resize-none"
                />
              </div>
            )}
            
            {/* Save Button */}
            <button 
              onClick={() => saveDashboardConfig(dashboardEditData.id, {
                hero: dashboardEditData.hero,
                feature_pills: dashboardEditData.feature_pills,
                action_cards: dashboardEditData.action_cards,
                checklist_items: dashboardEditData.checklist_items,
                widgets: dashboardEditData.widgets
              })}
              disabled={saving}
              className="w-full mt-6 py-3 bg-[#b08b5c] text-black rounded-lg font-medium hover:bg-[#9a7a4e] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save {selectedDashboardConfig.role_professional} Config
            </button>
          </div>
          
          {/* Right - Preview */}
          <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6">
            <h3 className="text-sm font-medium text-white/70 mb-4">Preview</h3>
            <div className="bg-[#0a0a0f] rounded-lg p-4 space-y-4">
              {/* Hero Preview */}
              <div>
                <p className="text-xs text-white/40 mb-1">Hero</p>
                <h2 className="text-lg font-light">
                  {dashboardEditData.hero?.title}{' '}
                  <span className="italic font-serif text-[#b08b5c]">{dashboardEditData.hero?.title_accent}</span>{' '}
                  {dashboardEditData.hero?.title_suffix}
                </h2>
                <p className="text-xs text-white/50 mt-1">{dashboardEditData.hero?.subtitle}</p>
              </div>
              
              {/* Feature Pills Preview */}
              {dashboardEditData.feature_pills?.length > 0 && (
                <div>
                  <p className="text-xs text-white/40 mb-2">Feature Pills</p>
                  <div className="flex gap-2 flex-wrap">
                    {dashboardEditData.feature_pills.map((pill, idx) => (
                      <div key={idx} className="px-3 py-1 bg-white/5 rounded text-xs">
                        <span className="text-[#b08b5c]">{pill.icon}</span> {pill.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Cards Preview */}
              {dashboardEditData.action_cards?.length > 0 && (
                <div>
                  <p className="text-xs text-white/40 mb-2">Action Cards</p>
                  <div className="grid grid-cols-2 gap-2">
                    {dashboardEditData.action_cards.map((card, idx) => (
                      <div key={idx} className="p-2 bg-white/5 rounded text-xs">
                        <p className="font-medium">{card.title}</p>
                        <p className="text-white/40 text-[10px]">{card.route}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Config Info */}
            <div className="mt-4 p-3 bg-[#2a2a28] rounded text-xs">
              <p><span className="text-white/40">ID:</span> {selectedDashboardConfig.id}</p>
              <p><span className="text-white/40">Role:</span> {selectedDashboardConfig.role_professional}</p>
              <p><span className="text-white/40">User Type:</span> {selectedDashboardConfig.user_type || 'any'}</p>
              <p><span className="text-white/40">Lang:</span> {selectedDashboardConfig.lang}</p>
              <p><span className="text-white/40">Active:</span> {selectedDashboardConfig.is_active ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {dashboardConfigs.length === 0 && (
        <div className="text-center py-12 text-white/40">
          <p>No dashboard configurations found.</p>
          <p className="text-sm mt-2">Run the SQL script to create initial configs.</p>
        </div>
      )}
    </div>
  );

  const renderHomepage = () => (
    <HomepageEditor showToast={showToast} />
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-4">
          <div className="text-2xl font-bold text-[#D6C1A5]">{users.length}</div>
          <div className="text-xs text-white/50">Total Users</div>
        </div>
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-4">
          <div className="text-2xl font-bold text-purple-400">{users.filter(u => u.role === 'admin').length}</div>
          <div className="text-xs text-white/50">Admins</div>
        </div>
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-4">
          <div className="text-2xl font-bold text-green-400">{users.filter(u => u.is_beta_user).length}</div>
          <div className="text-xs text-white/50">Beta Users</div>
        </div>
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-4">
          <div className="text-2xl font-bold text-orange-400">{users.filter(u => u.can_access_sre).length}</div>
          <div className="text-xs text-white/50">SRE Access</div>
        </div>
      </div>

      <div className="bg-[#1a1a18] rounded-xl border border-white/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#2a2a28]">
            <tr>
              <th className="text-left p-4 text-white/50 font-medium">Email</th>
              <th className="text-left p-4 text-white/50 font-medium">Nome</th>
              <th className="text-left p-4 text-white/50 font-medium">Ruolo</th>
              <th className="text-left p-4 text-white/50 font-medium">Beta</th>
              <th className="text-left p-4 text-white/50 font-medium">SRE</th>
              <th className="text-left p-4 text-white/50 font-medium">Registrato</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-white/40">Nessun utente trovato</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="p-4 text-white/80">{u.email}</td>
                <td className="p-4 text-white/60">{u.full_name || '-'}</td>
                <td className="p-4">
                  <select 
                    value={u.role || 'user'} 
                    onChange={e => updateUser(u.id, { role: e.target.value })}
                    className="bg-[#2a2a28] border border-white/10 rounded px-2 py-1 text-xs"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="architect">Architect</option>
                    <option value="rep">Rep</option>
                  </select>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => updateUser(u.id, { is_beta_user: !u.is_beta_user })}
                    className={`px-2 py-1 rounded text-xs ${u.is_beta_user ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}
                  >
                    {u.is_beta_user ? 'Attivo' : 'No'}
                  </button>
                </td>
                <td className="p-4">
                  <button
                    onClick={() => updateUser(u.id, { can_access_sre: !u.can_access_sre })}
                    className={`px-2 py-1 rounded text-xs ${u.can_access_sre ? 'bg-orange-500/20 text-orange-400' : 'bg-white/10 text-white/40'}`}
                  >
                    {u.can_access_sre ? 'Attivo' : 'No'}
                  </button>
                </td>
                <td className="p-4 text-white/40 text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPlans = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {(plans.length > 0 ? plans : [
        { id: 'free', name: 'Free', price: 0, limits: { max_projects: 1, max_moodboards: 3 } },
        { id: 'starter', name: 'Starter', price: 29, limits: { max_projects: 5, max_moodboards: 10 } },
        { id: 'pro', name: 'Pro', price: 79, limits: { max_projects: 20, max_moodboards: 50 } },
        { id: 'enterprise', name: 'Enterprise', price: 199, limits: { max_projects: -1, max_moodboards: -1 } }
      ]).map(plan => (
        <div key={plan.id} className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#D6C1A5]">{plan.name}</h3>
            <span className="text-2xl font-bold">€{plan.price}</span>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-white/50">Max Projects</span>
              <span>{plan.limits?.max_projects === -1 ? '∞' : plan.limits?.max_projects || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Max Moodboards</span>
              <span>{plan.limits?.max_moodboards === -1 ? '∞' : plan.limits?.max_moodboards || 'N/A'}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ============================================================================
  // PROJECT TEMPLATES SECTION
  // ============================================================================

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateEditData({
      name: template.name || '',
      description: template.description || '',
      role_professional: template.role_professional || '',
      cover_image: template.cover_image || '',
      is_active: template.is_active ?? true,
      data: template.data || { materials: [], spaces: [] }
    });
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate || !templateEditData) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('project_templates')
        .update({
          name: templateEditData.name,
          description: templateEditData.description,
          role_professional: templateEditData.role_professional,
          cover_image: templateEditData.cover_image,
          is_active: templateEditData.is_active,
          data: templateEditData.data,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedTemplate.id);
      
      if (error) throw error;
      
      showToast('Template salvato con successo!');
      loadData();
    } catch (err) {
      console.error('Save template error:', err);
      showToast('Errore nel salvataggio', 'error');
    }
    setSaving(false);
  };

  const handleAddMaterialToTemplate = () => {
    if (!templateEditData) return;
    
    const newMaterial = {
      material_id: `material-${Date.now()}`,
      material_name: 'Nuovo Materiale',
      material_type: 'Marble',
      material_image: '',
      application: '',
      notes: ''
    };
    
    setTemplateEditData({
      ...templateEditData,
      data: {
        ...templateEditData.data,
        materials: [...(templateEditData.data?.materials || []), newMaterial]
      }
    });
  };

  const handleRemoveMaterialFromTemplate = (index) => {
    if (!templateEditData) return;
    
    const materials = [...(templateEditData.data?.materials || [])];
    materials.splice(index, 1);
    
    setTemplateEditData({
      ...templateEditData,
      data: {
        ...templateEditData.data,
        materials
      }
    });
  };

  const handleUpdateTemplateMaterial = (index, field, value) => {
    if (!templateEditData) return;
    
    const materials = [...(templateEditData.data?.materials || [])];
    materials[index] = { ...materials[index], [field]: value };
    
    setTemplateEditData({
      ...templateEditData,
      data: {
        ...templateEditData.data,
        materials
      }
    });
  };

  const renderProjectTemplates = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light">Project Templates</h2>
          <p className="text-white/50 text-sm mt-1">
            Gestisci i template demo per ogni ruolo professionale
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm transition-colors"
        >
          <RefreshCw size={16} />
          Ricarica
        </button>
      </div>

      {/* Template List */}
      <div className="grid grid-cols-3 gap-4">
        {projectTemplates.map(template => (
          <div
            key={template.id}
            onClick={() => handleSelectTemplate(template)}
            className={`p-4 rounded-xl border cursor-pointer transition-all ${
              selectedTemplate?.id === template.id
                ? 'border-[#D6C1A5] bg-[#D6C1A5]/10'
                : 'border-white/10 bg-[#1a1a18] hover:border-white/20'
            }`}
          >
            {/* Cover Image */}
            {template.cover_image && (
              <div className="aspect-video rounded-lg overflow-hidden mb-3">
                <img 
                  src={template.cover_image} 
                  alt={template.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {/* Info */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-sm">{template.name}</h3>
                <p className="text-xs text-[#D6C1A5] mt-0.5">{template.role_professional}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                template.is_active 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'bg-white/10 text-white/40'
              }`}>
                {template.is_active ? 'ATTIVO' : 'DISATTIVO'}
              </span>
            </div>
            
            {/* Material count */}
            <p className="text-xs text-white/40 mt-2">
              {template.data?.materials?.length || 0} materiali inclusi
            </p>
          </div>
        ))}
        
        {projectTemplates.length === 0 && (
          <div className="col-span-3 text-center py-12 text-white/40">
            <Briefcase size={48} className="mx-auto mb-4 opacity-30" />
            <p>Nessun template trovato.</p>
            <p className="text-sm mt-1">Esegui lo script SQL per creare i template.</p>
          </div>
        )}
      </div>

      {/* Template Editor */}
      {selectedTemplate && templateEditData && (
        <div className="bg-[#1a1a18] rounded-xl border border-white/10 p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium">
              Modifica: {selectedTemplate.name}
            </h3>
            <button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#D6C1A5] text-[#0a0a09] rounded-lg text-sm font-medium hover:bg-[#c4af93] transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Salva Template
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1.5">Nome Template</label>
                <input
                  type="text"
                  value={templateEditData.name}
                  onChange={e => setTemplateEditData({ ...templateEditData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#D6C1A5]"
                />
              </div>
              
              <div>
                <label className="block text-sm text-white/60 mb-1.5">Ruolo Professionale</label>
                <select
                  value={templateEditData.role_professional}
                  onChange={e => setTemplateEditData({ ...templateEditData, role_professional: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#D6C1A5]"
                >
                  <option value="architect">Architect</option>
                  <option value="interior_designer">Interior Designer</option>
                  <option value="contractor">Contractor</option>
                  <option value="developer">Developer</option>
                  <option value="sales_rep">Sales Rep</option>
                  <option value="supplier">Supplier</option>
                  <option value="default">Default (Fallback)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-white/60 mb-1.5">Descrizione</label>
                <textarea
                  value={templateEditData.description}
                  onChange={e => setTemplateEditData({ ...templateEditData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#D6C1A5] resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-white/60 mb-1.5">Immagine di Copertina (URL)</label>
                <input
                  type="text"
                  value={templateEditData.cover_image}
                  onChange={e => setTemplateEditData({ ...templateEditData, cover_image: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#D6C1A5]"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="template-active"
                  checked={templateEditData.is_active}
                  onChange={e => setTemplateEditData({ ...templateEditData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5"
                />
                <label htmlFor="template-active" className="text-sm text-white/70">
                  Template attivo
                </label>
              </div>
            </div>

            {/* Right Column - Materials */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-white/60">Materiali inclusi</label>
                <button
                  onClick={handleAddMaterialToTemplate}
                  className="flex items-center gap-1 text-xs text-[#D6C1A5] hover:text-[#c4af93]"
                >
                  <Plus size={14} />
                  Aggiungi
                </button>
              </div>
              
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {(templateEditData.data?.materials || []).map((material, idx) => (
                  <div key={idx} className="p-3 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-start justify-between mb-2">
                      <input
                        type="text"
                        value={material.material_name}
                        onChange={e => handleUpdateTemplateMaterial(idx, 'material_name', e.target.value)}
                        placeholder="Nome materiale"
                        className="flex-1 px-2 py-1 bg-transparent border-b border-white/10 text-sm focus:outline-none focus:border-[#D6C1A5]"
                      />
                      <button
                        onClick={() => handleRemoveMaterialFromTemplate(idx)}
                        className="ml-2 text-white/30 hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <input
                        type="text"
                        value={material.material_type || ''}
                        onChange={e => handleUpdateTemplateMaterial(idx, 'material_type', e.target.value)}
                        placeholder="Tipo (es. Marble)"
                        className="px-2 py-1 bg-white/5 border border-white/5 rounded text-xs focus:outline-none"
                      />
                      <input
                        type="text"
                        value={material.application || ''}
                        onChange={e => handleUpdateTemplateMaterial(idx, 'application', e.target.value)}
                        placeholder="Applicazione"
                        className="px-2 py-1 bg-white/5 border border-white/5 rounded text-xs focus:outline-none"
                      />
                    </div>
                    
                    <input
                      type="text"
                      value={material.material_image || ''}
                      onChange={e => handleUpdateTemplateMaterial(idx, 'material_image', e.target.value)}
                      placeholder="URL immagine"
                      className="w-full px-2 py-1 bg-white/5 border border-white/5 rounded text-xs mt-2 focus:outline-none"
                    />
                  </div>
                ))}
                
                {(!templateEditData.data?.materials || templateEditData.data.materials.length === 0) && (
                  <p className="text-center text-white/30 text-sm py-4">
                    Nessun materiale. Clicca "Aggiungi" per iniziare.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================================================
  // DNA MATRIX SECTION
  // ============================================================================
  
  const loadDnaStatus = async () => {
    setDnaLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dna/status`);
      if (response.ok) {
        const data = await response.json();
        setDnaStatus(data);
      }
      
      // Load recent DNA records from Supabase
      if (supabase) {
        const { data: recentData } = await supabase
          .from('material_visual_dna')
          .select('material_id, analysis_status, dominant_palette, created_at')
          .order('created_at', { ascending: false })
          .limit(10);
        
        setRecentDna(recentData || []);
      }
    } catch (err) {
      console.error('DNA status error:', err);
    }
    setDnaLoading(false);
  };
  
  useEffect(() => {
    if (activeSection === 'dna-matrix') {
      loadDnaStatus();
    }
  }, [activeSection]);
  
  const renderDnaMatrix = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light flex items-center gap-3">
            <Hexagon className="w-7 h-7 text-[#D6C1A5]" />
            Material DNA Matrix
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Stato dell'analisi visiva dei materiali
          </p>
        </div>
        <button
          onClick={loadDnaStatus}
          disabled={dnaLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={dnaLoading ? 'animate-spin' : ''} />
          Aggiorna
        </button>
      </div>
      
      {/* Status Cards */}
      {dnaStatus && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Database size={20} className="text-[var(--gold)]" />
              <span className="text-xs text-white/50 uppercase tracking-wider">Totale</span>
            </div>
            <div className="text-3xl font-bold text-white">{dnaStatus.total_materials}</div>
            <p className="text-xs text-white/40 mt-1">materiali nel catalogo</p>
          </div>
          
          <div className="bg-[#1a1a18] rounded-xl border border-emerald-500/20 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Check size={20} className="text-emerald-400" />
              <span className="text-xs text-white/50 uppercase tracking-wider">Analizzati</span>
            </div>
            <div className="text-3xl font-bold text-emerald-400">{dnaStatus.with_dna}</div>
            <p className="text-xs text-white/40 mt-1">
              {dnaStatus.total_materials > 0 
                ? `${((dnaStatus.with_dna / dnaStatus.total_materials) * 100).toFixed(1)}% completato`
                : '0% completato'
              }
            </p>
          </div>
          
          <div className="bg-[#1a1a18] rounded-xl border border-yellow-500/20 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Clock size={20} className="text-yellow-400" />
              <span className="text-xs text-white/50 uppercase tracking-wider">In Coda</span>
            </div>
            <div className="text-3xl font-bold text-yellow-400">{dnaStatus.pending}</div>
            <p className="text-xs text-white/40 mt-1">in attesa di analisi</p>
          </div>
          
          <div className="bg-[#1a1a18] rounded-xl border border-red-500/20 p-5">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle size={20} className="text-red-400" />
              <span className="text-xs text-white/50 uppercase tracking-wider">Falliti</span>
            </div>
            <div className="text-3xl font-bold text-red-400">{dnaStatus.failed}</div>
            <p className="text-xs text-white/40 mt-1">errori di analisi</p>
          </div>
          
          <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
            <div className="flex items-center gap-3 mb-2">
              <Image size={20} className="text-white/40" />
              <span className="text-xs text-white/50 uppercase tracking-wider">Senza Img</span>
            </div>
            <div className="text-3xl font-bold text-white/40">{dnaStatus.without_image}</div>
            <p className="text-xs text-white/40 mt-1">nessuna immagine</p>
          </div>
        </div>
      )}
      
      {/* Progress Bar */}
      {dnaStatus && dnaStatus.total_materials > 0 && (
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white/70">Progresso Analisi DNA</h3>
            <span className="text-sm text-[#D6C1A5]">
              {((dnaStatus.with_dna / dnaStatus.total_materials) * 100).toFixed(1)}%
            </span>
          </div>
          <div className="h-3 bg-white/5 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#D6C1A5] to-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${(dnaStatus.with_dna / dnaStatus.total_materials) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-white/40 mt-2">
            <span>{dnaStatus.with_dna} completati</span>
            <span>{dnaStatus.total_materials - dnaStatus.with_dna - dnaStatus.failed} rimanenti</span>
          </div>
        </div>
      )}
      
      {/* Recent DNA Records */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
          <h3 className="text-sm font-medium text-white/70 mb-4">Analisi Recenti</h3>
          {recentDna.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {recentDna.map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {/* Palette Preview */}
                    {item.dominant_palette && item.dominant_palette.length > 0 ? (
                      <div className="flex -space-x-1">
                        {item.dominant_palette.slice(0, 5).map((color, i) => (
                          <div 
                            key={i}
                            className="w-5 h-5 rounded-full border border-white/20"
                            style={{ backgroundColor: color.hex }}
                            title={`${color.hex} (${(color.percentage * 100).toFixed(0)}%)`}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-white/10" />
                    )}
                    
                    <div>
                      <p className="text-sm text-white/80 font-mono truncate max-w-[180px]">
                        {item.material_id}
                      </p>
                      <p className="text-xs text-white/40">
                        {new Date(item.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  </div>
                  
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${
                    item.analysis_status === 'completed' 
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : item.analysis_status === 'pending'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'
                  }`}>
                    {item.analysis_status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-white/30 py-8">
              Nessun record DNA trovato
            </p>
          )}
        </div>
        
        {/* Quick Actions */}
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-5">
          <h3 className="text-sm font-medium text-white/70 mb-4">Azioni Rapide</h3>
          <div className="space-y-3">
            <a 
              href="/image-match"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Camera size={20} className="text-[#D6C1A5]" />
              <div>
                <p className="text-sm font-medium">Visual Match</p>
                <p className="text-xs text-white/40">Trova materiali simili via immagine</p>
              </div>
              <ExternalLink size={14} className="ml-auto text-white/30" />
            </a>
            
            <button 
              onClick={() => {
                showToast('Batch processing avviato!');
                // Could trigger batch processing here
              }}
              className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-left"
            >
              <Zap size={20} className="text-yellow-400" />
              <div>
                <p className="text-sm font-medium">Batch Analyze</p>
                <p className="text-xs text-white/40">Analizza tutti i materiali mancanti</p>
              </div>
            </button>
            
            <button 
              onClick={() => setActiveSection('materials')}
              className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-left"
            >
              <Database size={20} className="text-[var(--gold)]" />
              <div>
                <p className="text-sm font-medium">Gestisci Materiali</p>
                <p className="text-xs text-white/40">Vai al catalogo materiali</p>
              </div>
            </button>
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {dnaLoading && !dnaStatus && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-[#D6C1A5]" />
        </div>
      )}
    </div>
  );

  const renderPlaceholder = (title) => (
    <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-8 text-center">
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      <p className="text-white/50">Questa sezione sarà disponibile prossimamente.</p>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-[#0a0a09] text-white flex items-center justify-center">
        <div className="text-center">
          <Database size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Supabase Non Configurato</h1>
        </div>
      </div>
    );
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a09] text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#D6C1A5] mx-auto mb-4" />
          <p className="text-white/50">Verifica autenticazione...</p>
        </div>
      </div>
    );
  }

  // Block access if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a09] text-white flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Accesso Negato</h1>
          <p className="text-white/50 mb-4">Devi essere autenticato per accedere al Blueprint CMS</p>
          <button 
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-[#D6C1A5] text-[#0a0a09] rounded-lg font-medium hover:bg-[#c4a07a]"
          >
            Vai al Login
          </button>
        </div>
      </div>
    );
  }

  // Wait for profile to load
  if (!profile) {
    return (
      <div className="min-h-screen bg-[#0a0a09] text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#D6C1A5] mx-auto mb-4" />
          <p className="text-white/50">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  // Show loading while auth initializes
  if (authLoading || !authInitialized) {
    return (
      <div className="min-h-screen bg-[#0a0a09] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#D6C1A5] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60">Verifica accesso...</p>
        </div>
      </div>
    );
  }

  // Block access if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a09] text-white flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Accesso Riservato agli Admin</h1>
          <p className="text-white/50 mb-2">Il tuo ruolo: {profile?.role_system || 'non definito'}</p>
          <p className="text-white/50 mb-4">Solo gli utenti con ruolo "admin" possono accedere al Blueprint CMS</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-[#D6C1A5] text-[#0a0a09] rounded-lg font-medium hover:bg-[#c4a07a]"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a09] text-white flex">
      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-56'} border-r border-white/10 flex flex-col transition-all duration-300`}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {!sidebarCollapsed && (
            <>
              <span className="text-lg font-bold text-[#D6C1A5]">SLAB</span>
              <span className="text-lg font-light">REALITY</span>
            </>
          )}
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu size={18} />
          </button>
        </div>
        
        <nav className="flex-1 p-2 space-y-1">
          {SIDEBAR_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              title={sidebarCollapsed ? item.label : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                activeSection === item.id
                  ? 'bg-[#D6C1A5]/10 text-[#D6C1A5]'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <item.icon size={18} />
              {!sidebarCollapsed && item.label}
            </button>
          ))}
        </nav>
        
        {/* User */}
        <div className={`p-3 border-t border-white/10 ${sidebarCollapsed ? 'flex flex-col items-center' : ''}`}>
          <div className={`flex items-center gap-3 mb-3 ${sidebarCollapsed ? 'flex-col' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D6C1A5] to-[#8B7355] flex items-center justify-center text-[#0a0a09] font-bold flex-shrink-0">
              {user?.email?.[0]?.toUpperCase() || 'A'}
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.email?.split('@')[0] || 'Guest'}</div>
                <div className="text-xs text-white/40 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  Online
                </div>
              </div>
            )}
          </div>
          <button 
            onClick={handleLogout}
            title={sidebarCollapsed ? 'Logout' : undefined}
            className={`w-full py-2 bg-[#1a1a18] hover:bg-red-500/20 hover:text-red-400 rounded-lg text-xs flex items-center justify-center gap-2 transition-colors ${sidebarCollapsed ? 'px-2' : ''}`}
          >
            <LogOut size={14} />
            {!sidebarCollapsed && 'Logout'}
          </button>
        </div>
      </div>
      
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">SLABREALITY BLUEPRINT</h1>
            <p className="text-sm text-white/40">Command Center</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadData} className="p-2 hover:bg-white/5 rounded-lg" title="Refresh">
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <a 
              href="/" 
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 bg-[#D6C1A5] text-[#0a0a09] rounded-lg text-sm font-medium hover:bg-[#c4a07a] flex items-center gap-2"
            >
              VIEW SITE
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
        
        <div className="p-4">
          <TopStatsBar />
        </div>
        
        <div className="flex-1 p-4 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 size={32} className="animate-spin text-[#D6C1A5]" />
            </div>
          ) : (
            <>
              {activeSection === 'overview' && renderOverview()}
              {activeSection === 'dashboard-config' && renderDashboardConfig()}
              {activeSection === 'project-templates' && renderProjectTemplates()}
              {activeSection === 'homepage' && renderHomepage()}
              {activeSection === 'pages' && <BlueprintPagesManager />}
              {activeSection === 'sidebar-config' && <SidebarConfigEditor />}
              {activeSection === 'user-menu-config' && <UserMenuConfigEditor />}
              {activeSection === 'professionals' && (
                <ProfessionalsSection />
              )}
              {activeSection === 'dna-compare' && (
                <DnaCompareSection />
              )}
              {activeSection === 'posts' && (
                <PostsSection />
              )}
              {activeSection === 'support' && <AdminSupportPanel adminUser={user} />}
              {activeSection === 'users' && renderUsers()}
              {activeSection === 'plans' && renderPlans()}
              {activeSection === 'materials' && (
                <MaterialsSection />
              )}
              {activeSection === 'logs' && renderPlaceholder('System Logs')}
              {activeSection === 'translations' && renderPlaceholder('Translations')}
              {activeSection === 'dna-matrix' && renderDnaMatrix()}
              {activeSection === 'sre-engine' && renderPlaceholder('SRE Engine')}
            </>
          )}
        </div>
      </div>
      
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          {toast.type === 'error' ? <X size={16} /> : <Check size={16} />}
          <span className="text-sm font-medium">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 hover:opacity-70"><X size={14} /></button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PROFESSIONALS SECTION (TABS: MANAGEMENT + CONFIG)
// ============================================================================

const ProfessionalsSection = () => {
  const [activeTab, setActiveTab] = useState('management');
  
  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2a2a28] pb-4">
        <button
          onClick={() => setActiveTab('management')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'management' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          <Building2 size={14} className="inline mr-2" />
          Gestione Professionisti
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'config' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          <Settings size={14} className="inline mr-2" />
          Configurazione CMS
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'management' && <AdminProfessionalsManagement />}
      {activeTab === 'config' && <AdminProfessionalsConfig />}
    </div>
  );
};

// ============================================================================
// HERO EDITOR WITH IMAGE UPLOAD + CROP
// ============================================================================

const HeroEditor = ({ data, onChange, onSave, saving, showToast }) => {
  const [imageMode, setImageMode] = useState('preview');
  const [originalDataUrl, setOriginalDataUrl] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      setOriginalDataUrl(reader.result);
      setImageMode('editing');
      setCrop(null);
      setCompletedCrop(null);
      setBrightness(100);
      setContrast(100);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async () => {
    if (!imageRef.current || !canvasRef.current) {
      showToast('Errore: componenti non pronti', 'error');
      return;
    }
    
    setUploading(true);
    
    try {
      const image = imageRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      const pixelCrop = completedCrop ? {
        x: completedCrop.x * scaleX,
        y: completedCrop.y * scaleY,
        width: completedCrop.width * scaleX,
        height: completedCrop.height * scaleY
      } : {
        x: 0, y: 0,
        width: image.naturalWidth,
        height: image.naturalHeight
      };
      
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
      ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
      
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      
      // Upload to R2 via backend API
      const formData = new FormData();
      formData.append('file', blob, 'hero_image.jpg');
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      console.log('Uploading to R2 via:', `${backendUrl}/api/media/upload/cms-image`);
      
      const response = await fetch(`${backendUrl}/api/media/upload/cms-image`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error('Upload fallito');
      }
      
      const uploadData = await response.json();
      console.log('R2 Upload success:', uploadData.url);
      
      onChange({ ...data, image: uploadData.url });
      setImageMode('preview');
      setOriginalDataUrl(null);
      showToast('Immagine caricata!');
      
    } catch (err) {
      console.error('Upload error:', err);
      showToast('Errore upload: ' + err.message, 'error');
    }
    
    setUploading(false);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Zap className="text-[#D6C1A5]" size={20} />
        Hero Section
      </h3>
      
      <div>
        <label className="text-xs text-white/50 block mb-1">Label (sopra il titolo)</label>
        <input 
          type="text" 
          value={data.label || ''}
          onChange={e => onChange({...data, label: e.target.value})}
          className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm"
          placeholder="SRE ENGINE — PROIEZIONE DETERMINISTICA"
        />
      </div>
      
      {/* RIGA 1 */}
      <div className="p-4 bg-[#1a1a18] rounded-lg border border-white/10 space-y-3">
        <div className="text-xs text-[#D6C1A5] font-medium">RIGA 1</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-white/50 block mb-1">Testo Riga 1</label>
            <input 
              type="text" 
              value={data.line1 || ''}
              onChange={e => onChange({...data, line1: e.target.value})}
              className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm"
              placeholder="La materia reale."
            />
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Highlight <span className="text-[#D6C1A5]">(oro)</span></label>
            <input 
              type="text" 
              value={data.line1Highlight || ''}
              onChange={e => onChange({...data, line1Highlight: e.target.value})}
              className="w-full bg-[#2a2a28] border border-[#D6C1A5]/30 rounded-lg px-4 py-2 text-sm text-[#D6C1A5]"
              placeholder="reale"
            />
          </div>
        </div>
      </div>
      
      {/* RIGA 2 */}
      <div className="p-4 bg-[#1a1a18] rounded-lg border border-white/10 space-y-3">
        <div className="text-xs text-[#D6C1A5] font-medium">RIGA 2</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-white/50 block mb-1">Testo Riga 2</label>
            <input 
              type="text" 
              value={data.line2 || ''}
              onChange={e => onChange({...data, line2: e.target.value})}
              className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm"
              placeholder="Nel tuo spazio reale."
            />
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Highlight <span className="text-[#D6C1A5]">(oro)</span></label>
            <input 
              type="text" 
              value={data.line2Highlight || ''}
              onChange={e => onChange({...data, line2Highlight: e.target.value})}
              className="w-full bg-[#2a2a28] border border-[#D6C1A5]/30 rounded-lg px-4 py-2 text-sm text-[#D6C1A5]"
              placeholder="reale"
            />
          </div>
        </div>
      </div>
      
      <div>
        <label className="text-xs text-white/50 block mb-1">Description</label>
        <textarea 
          value={data.description || ''}
          onChange={e => onChange({...data, description: e.target.value})}
          rows={2}
          className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm resize-none"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-white/50 block mb-1">CTA Text</label>
          <input 
            type="text" 
            value={typeof data.cta === 'object' ? data.cta?.text : data.cta || ''}
            onChange={e => onChange({...data, cta: { text: e.target.value, route: typeof data.cta === 'object' ? data.cta?.route : '/sre' }})}
            className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm"
            placeholder="Avvia SRE"
          />
        </div>
        <div>
          <label className="text-xs text-white/50 block mb-1">CTA Route</label>
          <input 
            type="text" 
            value={typeof data.cta === 'object' ? data.cta?.route : '/sre'}
            onChange={e => onChange({...data, cta: { text: typeof data.cta === 'object' ? data.cta?.text : '', route: e.target.value }})}
            className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm"
            placeholder="/sre"
          />
        </div>
      </div>
      
      {/* IMAGE UPLOAD */}
      <div className="border border-white/10 rounded-lg p-4 bg-[#0a0a09]">
        <label className="text-xs text-white/50 block mb-3 flex items-center gap-2">
          <Image size={14} />
          Hero Image
        </label>
        
        {imageMode === 'preview' ? (
          <>
            {data.image ? (
              <div className="relative rounded-lg overflow-hidden mb-3">
                <img src={data.image} alt="Hero" className="w-full h-40 object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-[#D6C1A5] text-[#0a0a09] rounded text-sm font-medium">
                    Cambia
                  </button>
                  <button onClick={() => onChange({...data, image: ''})} className="px-3 py-1.5 bg-red-500 text-white rounded text-sm font-medium">
                    Rimuovi
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#D6C1A5] hover:bg-[#D6C1A5]/5 transition-colors mb-3"
              >
                <Upload size={24} className="text-white/40" />
                <span className="text-sm text-white/50">Carica Immagine</span>
              </button>
            )}
            
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            
            <div>
              <label className="text-xs text-white/40 block mb-1">oppure URL</label>
              <input
                type="text"
                value={data.image || ''}
                onChange={e => onChange({...data, image: e.target.value})}
                placeholder="https://..."
                className="w-full bg-[#2a2a28] border border-white/10 rounded px-3 py-2 text-sm"
              />
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2"><Crop size={14} className="text-[#D6C1A5]" /> Editor Immagine</span>
              <button onClick={() => { setImageMode('preview'); setOriginalDataUrl(null); }} className="text-white/50 hover:text-white"><X size={16} /></button>
            </div>
            
            <div className="bg-black rounded-lg p-2 max-h-[300px] overflow-auto">
              <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)} aspect={16/10}>
                <img ref={imageRef} src={originalDataUrl} alt="Edit" className="max-h-[280px] w-auto mx-auto" style={{ filter: `brightness(${brightness}%) contrast(${contrast}%)` }} />
              </ReactCrop>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-white/50 flex items-center gap-1 mb-1"><Sun size={12} /> Luminosità: {brightness}%</label>
                <input type="range" min="50" max="150" value={brightness} onChange={e => setBrightness(Number(e.target.value))} className="w-full accent-[#D6C1A5]" />
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1 block">Contrasto: {contrast}%</label>
                <input type="range" min="50" max="150" value={contrast} onChange={e => setContrast(Number(e.target.value))} className="w-full accent-[#D6C1A5]" />
              </div>
            </div>
            
            <div className="flex gap-2">
              <button onClick={() => { setImageMode('preview'); setOriginalDataUrl(null); }} className="flex-1 py-2 bg-[#2a2a28] rounded-lg text-sm hover:bg-[#3a3a38]">Annulla</button>
              <button onClick={handleImageUpload} disabled={uploading} className="flex-1 py-2 bg-[#D6C1A5] text-[#0a0a09] rounded-lg text-sm font-medium hover:bg-[#c4a07a] disabled:opacity-50 flex items-center justify-center gap-2">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {uploading ? 'Caricamento...' : 'Applica'}
              </button>
            </div>
            
            <canvas ref={canvasRef} className="hidden" />
          </div>
        )}
      </div>
      
      <button onClick={onSave} disabled={saving} className="w-full py-3 bg-[#D6C1A5] text-[#0a0a09] rounded-lg font-medium hover:bg-[#c4a07a] disabled:opacity-50 flex items-center justify-center gap-2">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Salva Hero
      </button>
    </div>
  );
};

// ============================================================================
// DISCOVERY EDITOR WITH ICON SELECTOR
// ============================================================================

const DiscoveryEditor = ({ data, onChange, onSave, saving, showToast }) => {
  const [showIconPicker, setShowIconPicker] = useState(null);

  const updateCard = (index, updates) => {
    const cards = [...(data.cards || [])];
    cards[index] = { ...cards[index], ...updates };
    onChange({ ...data, cards });
  };

  const addCard = () => {
    const cards = [...(data.cards || [])];
    cards.push({ title: 'New Card', description: '', icon: 'grid', route: '/' });
    onChange({ ...data, cards });
  };

  const removeCard = (index) => {
    const cards = [...(data.cards || [])];
    cards.splice(index, 1);
    onChange({ ...data, cards });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Discovery Cards</h3>
      
      <div>
        <label className="text-xs text-white/50 block mb-1">Section Label</label>
        <input 
          type="text" 
          value={data.label || ''}
          onChange={e => onChange({...data, label: e.target.value})}
          className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm"
        />
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs text-white/50">Cards ({data.cards?.length || 0})</label>
          <button onClick={addCard} className="px-2 py-1 bg-[#D6C1A5]/20 text-[#D6C1A5] rounded text-xs hover:bg-[#D6C1A5]/30">
            + Add Card
          </button>
        </div>
        
        {(data.cards || []).map((card, i) => (
          <div key={i} className="p-4 bg-[#2a2a28] rounded-lg space-y-3 relative">
            <button 
              onClick={() => removeCard(i)}
              className="absolute top-2 right-2 p-1 text-red-400 hover:bg-red-400/20 rounded"
            >
              <X size={14} />
            </button>
            
            {/* Icon Selector */}
            <div>
              <label className="text-xs text-white/50 block mb-2">Icon</label>
              <div className="relative">
                <button
                  onClick={() => setShowIconPicker(showIconPicker === i ? null : i)}
                  className="w-full p-3 bg-[#1a1a18] border border-white/10 rounded-lg flex items-center gap-3 hover:border-[#D6C1A5]/50"
                >
                  {(() => {
                    const IconComp = ICON_LIBRARY.find(ic => ic.id === card.icon)?.icon || Grid3X3;
                    return <IconComp size={20} className="text-[#D6C1A5]" />;
                  })()}
                  <span className="text-sm text-white/70">{card.icon || 'Select icon'}</span>
                </button>
                
                {showIconPicker === i && (
                  <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-[#1a1a18] border border-white/10 rounded-lg z-10 grid grid-cols-6 gap-2 max-h-48 overflow-auto">
                    {ICON_LIBRARY.map(({ id, icon: Icon, label }) => (
                      <button
                        key={id}
                        onClick={() => { updateCard(i, { icon: id }); setShowIconPicker(null); }}
                        className={`p-2 rounded hover:bg-[#D6C1A5]/20 flex flex-col items-center gap-1 ${card.icon === id ? 'bg-[#D6C1A5]/20' : ''}`}
                        title={label}
                      >
                        <Icon size={18} className="text-[#D6C1A5]" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/50 block mb-1">Title</label>
                <input 
                  type="text" 
                  value={card.title || ''}
                  onChange={e => updateCard(i, { title: e.target.value })}
                  className="w-full bg-[#1a1a18] border border-white/10 rounded px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1">Route</label>
                <input 
                  type="text" 
                  value={card.route || ''}
                  onChange={e => updateCard(i, { route: e.target.value })}
                  className="w-full bg-[#1a1a18] border border-white/10 rounded px-3 py-1.5 text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="text-xs text-white/50 block mb-1">Description</label>
              <input 
                type="text" 
                value={card.description || ''}
                onChange={e => updateCard(i, { description: e.target.value })}
                className="w-full bg-[#1a1a18] border border-white/10 rounded px-3 py-1.5 text-sm"
              />
            </div>
          </div>
        ))}
      </div>
      
      <button onClick={onSave} disabled={saving} className="w-full py-3 bg-[#D6C1A5] text-[#0a0a09] rounded-lg font-medium hover:bg-[#c4a07a] disabled:opacity-50 flex items-center justify-center gap-2">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Salva Discovery
      </button>
    </div>
  );
};

// ============================================================================
// OTHER EDITORS
// ============================================================================

const StatsEditor = ({ data, onChange, onSave, saving }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold">Stats Bar</h3>
    <pre className="bg-[#0a0a09] p-3 rounded text-xs text-white/70 overflow-auto max-h-48">
      {JSON.stringify(data, null, 2)}
    </pre>
    <button onClick={onSave} disabled={saving} className="w-full py-3 bg-[#D6C1A5] text-[#0a0a09] rounded-lg font-medium hover:bg-[#c4a07a] disabled:opacity-50 flex items-center justify-center gap-2">
      {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
      Salva Stats
    </button>
  </div>
);

const CTAEditor = ({ data, onChange, onSave, saving }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold">CTA Section</h3>
    <div>
      <label className="text-xs text-white/50 block mb-1">Title</label>
      <input type="text" value={data.title || ''} onChange={e => onChange({...data, title: e.target.value})} className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm" />
    </div>
    <div>
      <label className="text-xs text-white/50 block mb-1">Subtitle</label>
      <textarea value={data.subtitle || ''} onChange={e => onChange({...data, subtitle: e.target.value})} rows={2} className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm resize-none" />
    </div>
    <div>
      <label className="text-xs text-white/50 block mb-1">Button Text</label>
      <input type="text" value={typeof data.cta === 'object' ? data.cta?.text : data.cta || ''} onChange={e => onChange({...data, cta: { text: e.target.value, route: data.cta?.route || '/register' }})} className="w-full bg-[#2a2a28] border border-white/10 rounded-lg px-4 py-2 text-sm" />
    </div>
    <button onClick={onSave} disabled={saving} className="w-full py-3 bg-[#D6C1A5] text-[#0a0a09] rounded-lg font-medium hover:bg-[#c4a07a] disabled:opacity-50 flex items-center justify-center gap-2">
      {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
      Salva CTA
    </button>
  </div>
);

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatBadge = ({ icon, label, value, valueColor }) => (
  <div className="flex items-center gap-3 px-2 flex-shrink-0">
    <div className="w-8 h-8 rounded-lg bg-[#2a2a28] flex items-center justify-center">{icon}</div>
    <div>
      <div className="text-[10px] text-white/40 uppercase">{label}</div>
      <div className={`text-sm font-semibold ${valueColor}`}>{value}</div>
    </div>
  </div>
);

// ============================================================================
// DNA COMPARE SECTION - CMS Configuration for DNA Compare Page
// ============================================================================

const DnaCompareSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  useEffect(() => {
    loadConfig();
  }, []);
  
  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('blueprint_dna_compare_config')
        .select('*')
        .eq('lang', 'it')
        .single();
      
      if (data) {
        setConfig(data);
      } else {
        // Create default config if not exists
        setConfig({
          lang: 'it',
          page_title: 'Confronta il DNA di due materiali',
          page_subtitle: 'Seleziona due materiali naturali da confrontare side-by-side e scopri differenze e somiglianze nelle loro caratteristiche visive e prestazionali.',
          step1_title: 'Seleziona i materiali',
          step1_desc: 'Scegli due materiali da confrontare',
          step2_title: 'Confronto DNA',
          step2_desc: 'Analisi delle caratteristiche',
          step3_title: 'Risultati',
          step3_desc: 'Insights e raccomandazioni',
          why_title: 'Perché confrontare due materiali?',
          why_subtitle: 'Il confronto DNA ti aiuta a valutare somiglianze, differenze e prestazioni per scegliere il materiale perfetto per il tuo progetto.',
          feature1_title: 'Analisi avanzata',
          feature1_desc: 'Algoritmi proprietari analizzano venature, colore, texture e struttura.',
          feature2_title: 'Confronto oggettivo',
          feature2_desc: 'Dati tecnici e visivi per decisioni informate e senza bias.',
          feature3_title: 'Supporto al progetto',
          feature3_desc: 'Ideale per architetti, designer e professionisti del settore.',
          feature4_title: 'Risultati immediati',
          feature4_desc: 'Insights chiari e raccomandazioni utili in pochi secondi.'
        });
      }
    } catch (err) {
      console.error('Error loading DNA compare config:', err);
    }
    setLoading(false);
  };
  
  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_dna_compare_config')
        .upsert({
          ...config,
          updated_at: new Date().toISOString()
        }, { onConflict: 'lang' });
      
      if (error) throw error;
      alert('Configurazione salvata con successo!');
    } catch (err) {
      console.error('Error saving config:', err);
      alert('Errore nel salvataggio: ' + err.message);
    }
    setSaving(false);
  };
  
  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };
  
  const tabs = [
    { id: 'general', label: 'Generale', icon: FileText },
    { id: 'steps', label: 'Steps', icon: Layers },
    { id: 'features', label: 'Features', icon: Sparkles },
    { id: 'technical', label: 'Tecnico', icon: Database },
    { id: 'cta', label: 'CTA', icon: MousePointer }
  ];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#D6C1A5]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-[#D6C1A5]" />
            DNA Compare CMS
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Gestisci i testi della pagina Confronta DNA
          </p>
        </div>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-[#D6C1A5] text-black rounded-lg text-sm font-medium hover:bg-[#c4af93] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Salva Configurazione
        </button>
      </div>
      
      {/* Info Box */}
      <div className="bg-[var(--gold)]/10 border border-[var(--gold)]/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[var(--gold)] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[var(--gold)] font-medium mb-1">Tabella Database Richiesta</h4>
            <p className="text-white/60 text-sm">
              Per utilizzare questa funzionalità, esegui lo script SQL disponibile qui:
            </p>
            <a 
              href="https://visual-search-hub-2.preview.emergentagent.com/supabase-dna-compare-config.sql"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--gold)] hover:underline text-sm flex items-center gap-1 mt-1"
            >
              <ExternalLink size={14} />
              Scarica SQL Script
            </a>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2a2a28] pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab.id 
                ? 'bg-[#D6C1A5] text-[#0a0a09]' 
                : 'bg-[#2a2a28] text-white/60 hover:text-white'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6">
        {activeTab === 'general' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white mb-4">Testi Generali</h3>
            
            <div>
              <label className="text-sm text-white/60 block mb-1">Titolo Pagina</label>
              <input
                type="text"
                value={config?.page_title || ''}
                onChange={(e) => updateField('page_title', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
              />
            </div>
            
            <div>
              <label className="text-sm text-white/60 block mb-1">Sottotitolo Pagina</label>
              <textarea
                value={config?.page_subtitle || ''}
                onChange={(e) => updateField('page_subtitle', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1">Titolo Why Compare</label>
                <input
                  type="text"
                  value={config?.why_title || ''}
                  onChange={(e) => updateField('why_title', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1">Pulsante Indietro</label>
                <input
                  type="text"
                  value={config?.back_button || ''}
                  onChange={(e) => updateField('back_button', e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm text-white/60 block mb-1">Sottotitolo Why Compare</label>
              <textarea
                value={config?.why_subtitle || ''}
                onChange={(e) => updateField('why_subtitle', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
              />
            </div>
          </div>
        )}
        
        {activeTab === 'steps' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-white mb-4">Indicatori Step</h3>
            
            {[1, 2, 3].map(num => (
              <div key={num} className="bg-[#0a0a09] rounded-lg p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-[#D6C1A5] text-black flex items-center justify-center text-sm font-bold">
                    {num}
                  </div>
                  <span className="text-white/70">Step {num}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/60 block mb-1">Titolo</label>
                    <input
                      type="text"
                      value={config?.[`step${num}_title`] || ''}
                      onChange={(e) => updateField(`step${num}_title`, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[#1a1a18] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/60 block mb-1">Descrizione</label>
                    <input
                      type="text"
                      value={config?.[`step${num}_desc`] || ''}
                      onChange={(e) => updateField(`step${num}_desc`, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[#1a1a18] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'features' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-white mb-4">Feature Cards</h3>
            
            {[1, 2, 3, 4].map(num => (
              <div key={num} className="bg-[#0a0a09] rounded-lg p-4 border border-white/5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={16} className="text-[#D6C1A5]" />
                  <span className="text-white/70">Feature {num}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/60 block mb-1">Titolo</label>
                    <input
                      type="text"
                      value={config?.[`feature${num}_title`] || ''}
                      onChange={(e) => updateField(`feature${num}_title`, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[#1a1a18] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/60 block mb-1">Descrizione</label>
                    <input
                      type="text"
                      value={config?.[`feature${num}_desc`] || ''}
                      onChange={(e) => updateField(`feature${num}_desc`, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[#1a1a18] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'technical' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white mb-4">Etichette Tecniche</h3>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'tech_origin', label: 'Origine' },
                { key: 'tech_type', label: 'Tipologia' },
                { key: 'tech_finish', label: 'Finitura disponibile' },
                { key: 'tech_thickness', label: 'Spessore standard' },
                { key: 'tech_water_absorption', label: 'Assorbimento acqua' },
                { key: 'tech_density', label: 'Densità' },
                { key: 'tech_flexural', label: 'Resistenza a flessione' },
                { key: 'tech_compressive', label: 'Resistenza a compressione' }
              ].map(field => (
                <div key={field.key}>
                  <label className="text-sm text-white/60 block mb-1">{field.label}</label>
                  <input
                    type="text"
                    value={config?.[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                  />
                </div>
              ))}
            </div>
            
            <h4 className="text-md font-medium text-white/80 mt-6 mb-3">Etichette "Ideale Per"</h4>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'ideal_kitchen', label: 'Top cucina' },
                { key: 'ideal_bathroom', label: 'Bagno' },
                { key: 'ideal_wall', label: 'Rivestimenti verticali' },
                { key: 'ideal_floor', label: 'Pavimenti interni' },
                { key: 'ideal_decorative', label: 'Pareti decorative' }
              ].map(field => (
                <div key={field.key}>
                  <label className="text-sm text-white/60 block mb-1">{field.label}</label>
                  <input
                    type="text"
                    value={config?.[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'cta' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white mb-4">Pulsanti e CTA</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/60 block mb-1">Pulsante Confronta</label>
                <input
                  type="text"
                  value={config?.compare_button || ''}
                  onChange={(e) => updateField('compare_button', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1">Pulsante Nuovo Confronto</label>
                <input
                  type="text"
                  value={config?.new_comparison || ''}
                  onChange={(e) => updateField('new_comparison', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1">CTA: Prova nel tuo spazio</label>
                <input
                  type="text"
                  value={config?.cta_try_space || ''}
                  onChange={(e) => updateField('cta_try_space', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1">CTA: Aggiungi al progetto</label>
                <input
                  type="text"
                  value={config?.cta_add_project || ''}
                  onChange={(e) => updateField('cta_add_project', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1">CTA: Chiedi all'Advisor</label>
                <input
                  type="text"
                  value={config?.cta_ask_advisor || ''}
                  onChange={(e) => updateField('cta_ask_advisor', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 block mb-1">Sfoglia materiali</label>
                <input
                  type="text"
                  value={config?.browse_button || ''}
                  onChange={(e) => updateField('browse_button', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// POSTS SECTION - Gestione articoli/blog
// ============================================================================

const PostsSection = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [editingPost, setEditingPost] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [editingTag, setEditingTag] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load posts with relations
      const { data: postsData } = await supabase
        .from('blueprint_posts')
        .select(`
          *,
          category:blueprint_post_categories(id, name_it, slug),
          author:blueprint_authors(id, name, avatar_url)
        `)
        .order('created_at', { ascending: false });
      
      if (postsData) setPosts(postsData);

      // Load categories
      const { data: catData } = await supabase
        .from('blueprint_post_categories')
        .select('*')
        .order('order_index');
      if (catData) setCategories(catData);

      // Load authors
      const { data: authorsData } = await supabase
        .from('blueprint_authors')
        .select('*')
        .order('name');
      if (authorsData) setAuthors(authorsData);

      // Load tags
      const { data: tagsData } = await supabase
        .from('blueprint_post_tags')
        .select('*')
        .order('name');
      if (tagsData) setTags(tagsData);
    } catch (err) {
      console.error('Error loading posts data:', err);
    }
    setLoading(false);
  };

  // Category CRUD
  const saveCategory = async (categoryData) => {
    const isNew = !categoryData.id;
    
    if (isNew) {
      if (!categoryData.slug) {
        categoryData.slug = (categoryData.name_it || categoryData.name || 'categoria')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      const { error } = await supabase.from('blueprint_post_categories').insert(categoryData);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    } else {
      const { error } = await supabase
        .from('blueprint_post_categories')
        .update(categoryData)
        .eq('id', categoryData.id);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    }
    
    setEditingCategory(null);
    loadData();
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa categoria?')) return;
    
    const { error } = await supabase.from('blueprint_post_categories').delete().eq('id', categoryId);
    if (!error) {
      setCategories(categories.filter(c => c.id !== categoryId));
    }
  };

  // Author CRUD
  const saveAuthor = async (authorData) => {
    const isNew = !authorData.id;
    
    if (isNew) {
      if (!authorData.slug) {
        authorData.slug = (authorData.name || 'autore')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      const { error } = await supabase.from('blueprint_authors').insert(authorData);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    } else {
      const { error } = await supabase
        .from('blueprint_authors')
        .update(authorData)
        .eq('id', authorData.id);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    }
    
    setEditingAuthor(null);
    loadData();
  };

  const deleteAuthor = async (authorId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo autore?')) return;
    
    const { error } = await supabase.from('blueprint_authors').delete().eq('id', authorId);
    if (!error) {
      setAuthors(authors.filter(a => a.id !== authorId));
    }
  };

  // Tag CRUD
  const saveTag = async (tagData) => {
    const isNew = !tagData.id;
    
    if (isNew) {
      if (!tagData.slug) {
        tagData.slug = (tagData.name || 'tag')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      const { error } = await supabase.from('blueprint_post_tags').insert(tagData);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    } else {
      const { error } = await supabase
        .from('blueprint_post_tags')
        .update(tagData)
        .eq('id', tagData.id);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    }
    
    setEditingTag(null);
    loadData();
  };

  const deleteTag = async (tagId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo tag?')) return;
    
    const { error } = await supabase.from('blueprint_post_tags').delete().eq('id', tagId);
    if (!error) {
      setTags(tags.filter(t => t.id !== tagId));
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo articolo?')) return;
    
    const { error } = await supabase.from('blueprint_posts').delete().eq('id', postId);
    if (!error) {
      setPosts(posts.filter(p => p.id !== postId));
    }
  };

  const updatePostStatus = async (postId, status) => {
    const updates = { status };
    if (status === 'published') {
      updates.published_at = new Date().toISOString();
    }
    
    const { error } = await supabase
      .from('blueprint_posts')
      .update(updates)
      .eq('id', postId);
    
    if (!error) {
      loadData();
    }
  };

  const savePost = async (postData) => {
    const isNew = !postData.id;
    
    if (isNew) {
      // Generate slug if not provided
      if (!postData.slug) {
        postData.slug = (postData.title_it || postData.title || 'post')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      
      const { error } = await supabase.from('blueprint_posts').insert(postData);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    } else {
      const { error } = await supabase
        .from('blueprint_posts')
        .update(postData)
        .eq('id', postData.id);
      if (error) {
        alert('Errore: ' + error.message);
        return;
      }
    }
    
    setEditingPost(null);
    loadData();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const tabs = [
    { id: 'posts', label: 'Articoli', count: posts.length },
    { id: 'categories', label: 'Categorie', count: categories.length },
    { id: 'authors', label: 'Autori', count: authors.length },
    { id: 'tags', label: 'Tag', count: tags.length }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#D6C1A5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light flex items-center gap-3">
            <Newspaper className="w-7 h-7 text-[#D6C1A5]" />
            Gestione Contenuti
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Articoli, categorie e autori per la sezione Ispirazione
          </p>
        </div>
        <button
          onClick={() => setEditingPost({ status: 'draft', reading_time: 5 })}
          className="flex items-center gap-2 px-4 py-2 bg-[#D6C1A5] text-black rounded-lg text-sm font-medium hover:bg-[#c4af93] transition-colors"
        >
          <Plus size={16} />
          Nuovo Articolo
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2a2a28] pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-[#D6C1A5] text-[#0a0a09]' 
                : 'bg-[#2a2a28] text-white/60 hover:text-white'
            }`}
          >
            {tab.label}
            <span className="ml-2 text-xs opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Posts Tab */}
      {activeTab === 'posts' && !editingPost && (
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0a0a09]">
              <tr>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Articolo</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Categoria</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Autore</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Stato</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Data</th>
                <th className="text-right px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {posts.map(post => (
                <tr key={post.id} className="hover:bg-white/5">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {post.cover_image_url ? (
                        <img src={post.cover_image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#2a2a28] flex items-center justify-center">
                          <Image size={16} className="text-white/20" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium text-sm">{post.title_it || post.title}</p>
                        <p className="text-white/40 text-xs">{post.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white/60 text-sm">{post.category?.name_it || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {post.author?.avatar_url && (
                        <img src={post.author.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                      )}
                      <span className="text-white/60 text-sm">{post.author?.name || '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={post.status}
                      onChange={(e) => updatePostStatus(post.id, e.target.value)}
                      className={`text-xs px-3 py-1.5 rounded-lg font-medium cursor-pointer ${
                        post.status === 'published' ? 'bg-green-500/20 text-green-400' :
                        post.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      } border border-white/10 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-[#D6C1A5]/50`}
                      style={{ appearance: 'auto' }}
                    >
                      <option value="draft">Bozza</option>
                      <option value="published">Pubblicato</option>
                      <option value="archived">Archiviato</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white/40 text-sm">{formatDate(post.published_at || post.created_at)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => setEditingPost(post)}
                        className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
                      >
                        <Edit size={16} />
                      </button>
                      <a 
                        href={`/inspiration/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button 
                        onClick={() => deletePost(post.id)}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-white/40">
                    Nessun articolo trovato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Post Form */}
      {editingPost && (
        <PostEditForm 
          post={editingPost} 
          categories={categories}
          authors={authors}
          onSave={savePost}
          onCancel={() => setEditingPost(null)}
        />
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && !editingCategory && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(cat => (
            <div key={cat.id} className="bg-[#1a1a18] rounded-xl border border-white/5 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: (cat.color || '#B08B5C') + '20', color: cat.color || '#B08B5C' }}
                  >
                    <Tag size={18} />
                  </div>
                  <div>
                    <p className="text-white font-medium">{cat.name_it || cat.name}</p>
                    <p className="text-white/40 text-xs">{cat.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => setEditingCategory(cat)}
                    className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
                  >
                    <Edit size={14} />
                  </button>
                  <button 
                    onClick={() => deleteCategory(cat.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="text-white/50 text-sm mb-3">{cat.description_it || 'Nessuna descrizione'}</p>
              <div className="flex items-center justify-between text-xs text-white/40">
                <span>{cat.post_count || 0} articoli</span>
                <div className="flex items-center gap-2">
                  {cat.show_in_homepage && <span className="px-1.5 py-0.5 rounded bg-[#D6C1A5]/20 text-[#D6C1A5]">Homepage</span>}
                  <span>Ordine: {cat.order_index}</span>
                </div>
              </div>
            </div>
          ))}
          <button 
            onClick={() => setEditingCategory({ order_index: categories.length + 1, color: '#B08B5C', is_active: true, show_in_homepage: true })}
            className="bg-[#1a1a18] rounded-xl border border-dashed border-white/10 p-4 flex items-center justify-center gap-2 text-white/40 hover:text-white hover:border-white/20 transition-colors min-h-[140px]"
          >
            <Plus size={18} />
            Nuova categoria
          </button>
        </div>
      )}

      {/* Category Edit Modal */}
      {editingCategory && (
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">
              {editingCategory.id ? 'Modifica Categoria' : 'Nuova Categoria'}
            </h3>
            <button onClick={() => setEditingCategory(null)} className="text-white/40 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/60 block mb-1">Nome (IT) *</label>
              <input
                type="text"
                value={editingCategory.name_it || ''}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, name_it: e.target.value, name: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Nome categoria"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">Slug</label>
              <input
                type="text"
                value={editingCategory.slug || ''}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, slug: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="url-categoria"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-white/60 block mb-1">Descrizione (IT)</label>
              <textarea
                value={editingCategory.description_it || ''}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, description_it: e.target.value }))}
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Descrizione breve..."
              />
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">Colore</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={editingCategory.color || '#B08B5C'}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 rounded-lg border-none cursor-pointer"
                />
                <input
                  type="text"
                  value={editingCategory.color || '#B08B5C'}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">Ordine</label>
              <input
                type="number"
                value={editingCategory.order_index || 1}
                onChange={(e) => setEditingCategory(prev => ({ ...prev, order_index: parseInt(e.target.value) }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                min={1}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingCategory.is_active ?? true}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-white/60">Attiva</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingCategory.show_in_homepage ?? true}
                  onChange={(e) => setEditingCategory(prev => ({ ...prev, show_in_homepage: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-white/60">Mostra in homepage</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
            <button
              onClick={() => saveCategory(editingCategory)}
              className="flex items-center justify-center gap-2 px-6 py-2 bg-[#D6C1A5] text-black rounded-lg text-sm font-medium hover:bg-[#c4af93]"
            >
              <Save size={16} />
              Salva
            </button>
            <button
              onClick={() => setEditingCategory(null)}
              className="px-4 py-2 border border-white/10 text-white/60 rounded-lg text-sm hover:bg-white/5"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Authors Tab */}
      {activeTab === 'authors' && !editingAuthor && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {authors.map(author => (
            <div key={author.id} className="bg-[#1a1a18] rounded-xl border border-white/5 p-4">
              <div className="flex items-start gap-4">
                {author.avatar_url ? (
                  <img src={author.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#2a2a28] flex items-center justify-center">
                    <User size={20} className="text-white/20" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{author.name}</p>
                      <p className="text-[#D6C1A5] text-xs">{author.role}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => setEditingAuthor(author)}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        onClick={() => deleteAuthor(author.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm mt-2 line-clamp-2">{author.bio_short || 'Nessuna bio'}</p>
                  <div className="flex items-center gap-3 mt-3">
                    {author.can_publish && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                        Può pubblicare
                      </span>
                    )}
                    {!author.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                        Disattivo
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button 
            onClick={() => setEditingAuthor({ can_publish: true, is_active: true })}
            className="bg-[#1a1a18] rounded-xl border border-dashed border-white/10 p-4 flex items-center justify-center gap-2 text-white/40 hover:text-white hover:border-white/20 transition-colors min-h-[140px]"
          >
            <Plus size={18} />
            Nuovo autore
          </button>
        </div>
      )}

      {/* Author Edit Modal */}
      {editingAuthor && (
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">
              {editingAuthor.id ? 'Modifica Autore' : 'Nuovo Autore'}
            </h3>
            <button onClick={() => setEditingAuthor(null)} className="text-white/40 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/60 block mb-1">Nome *</label>
              <input
                type="text"
                value={editingAuthor.name || ''}
                onChange={(e) => setEditingAuthor(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Nome completo"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">Ruolo</label>
              <input
                type="text"
                value={editingAuthor.role || ''}
                onChange={(e) => setEditingAuthor(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="es. Architetto, Interior Designer"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-white/60 block mb-1">Avatar URL</label>
              <input
                type="text"
                value={editingAuthor.avatar_url || ''}
                onChange={(e) => setEditingAuthor(prev => ({ ...prev, avatar_url: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="https://..."
              />
              {editingAuthor.avatar_url && (
                <img src={editingAuthor.avatar_url} alt="" className="mt-2 w-20 h-20 rounded-full object-cover" />
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-white/60 block mb-1">Bio breve</label>
              <textarea
                value={editingAuthor.bio_short || ''}
                onChange={(e) => setEditingAuthor(prev => ({ ...prev, bio_short: e.target.value }))}
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Breve descrizione professionale..."
              />
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">LinkedIn</label>
              <input
                type="text"
                value={editingAuthor.linkedin_url || ''}
                onChange={(e) => setEditingAuthor(prev => ({ ...prev, linkedin_url: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">Instagram</label>
              <input
                type="text"
                value={editingAuthor.instagram_url || ''}
                onChange={(e) => setEditingAuthor(prev => ({ ...prev, instagram_url: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="https://instagram.com/..."
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingAuthor.is_active ?? true}
                  onChange={(e) => setEditingAuthor(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-white/60">Attivo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingAuthor.can_publish ?? true}
                  onChange={(e) => setEditingAuthor(prev => ({ ...prev, can_publish: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-white/60">Può pubblicare</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
            <button
              onClick={() => saveAuthor(editingAuthor)}
              className="flex items-center justify-center gap-2 px-6 py-2 bg-[#D6C1A5] text-black rounded-lg text-sm font-medium hover:bg-[#c4af93]"
            >
              <Save size={16} />
              Salva
            </button>
            <button
              onClick={() => setEditingAuthor(null)}
              className="px-4 py-2 border border-white/10 text-white/60 rounded-lg text-sm hover:bg-white/5"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Tags Tab */}
      {activeTab === 'tags' && !editingTag && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {tags.map(tag => (
              <div 
                key={tag.id} 
                className="inline-flex items-center gap-2 px-3 py-2 bg-[#1a1a18] rounded-lg border border-white/5 group"
              >
                <span className="text-white text-sm">{tag.name}</span>
                <span className="text-white/30 text-xs">({tag.post_count || 0})</span>
                <button 
                  onClick={() => setEditingTag(tag)}
                  className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Edit size={12} />
                </button>
                <button 
                  onClick={() => deleteTag(tag.id)}
                  className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button 
              onClick={() => setEditingTag({ is_active: true })}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-white/10 text-white/40 hover:text-white hover:border-white/20 transition-colors"
            >
              <Plus size={14} />
              Nuovo tag
            </button>
          </div>
          {tags.length === 0 && (
            <div className="text-center py-12 text-white/40">
              Nessun tag trovato. Crea il primo tag per organizzare gli articoli.
            </div>
          )}
        </div>
      )}

      {/* Tag Edit Modal */}
      {editingTag && (
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6 max-w-md">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">
              {editingTag.id ? 'Modifica Tag' : 'Nuovo Tag'}
            </h3>
            <button onClick={() => setEditingTag(null)} className="text-white/40 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-white/60 block mb-1">Nome *</label>
              <input
                type="text"
                value={editingTag.name || ''}
                onChange={(e) => setEditingTag(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Nome tag"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 block mb-1">Slug</label>
              <input
                type="text"
                value={editingTag.slug || ''}
                onChange={(e) => setEditingTag(prev => ({ ...prev, slug: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="url-tag"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
            <button
              onClick={() => saveTag(editingTag)}
              className="flex items-center justify-center gap-2 px-6 py-2 bg-[#D6C1A5] text-black rounded-lg text-sm font-medium hover:bg-[#c4af93]"
            >
              <Save size={16} />
              Salva
            </button>
            <button
              onClick={() => setEditingTag(null)}
              className="px-4 py-2 border border-white/10 text-white/60 rounded-lg text-sm hover:bg-white/5"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Post Edit Form Component - With WYSIWYG & Hotspots
const PostEditForm = ({ post, categories, authors, onSave, onCancel }) => {
  const [formData, setFormData] = useState(post);
  const [activeTab, setActiveTab] = useState('content'); // content | hotspots
  const [hotspots, setHotspots] = useState([]);
  const [hotspotsLoading, setHotspotsLoading] = useState(false);

  // Load existing hotspots if editing
  useEffect(() => {
    if (post.id) {
      loadHotspots();
    }
  }, [post.id]);

  const loadHotspots = async () => {
    setHotspotsLoading(true);
    try {
      const { data } = await supabase
        .from('blueprint_post_hotspots')
        .select('*, material:materials(id, name, image_url, category)')
        .eq('post_id', post.id);
      if (data) setHotspots(data);
    } catch (err) {
      console.error('Error loading hotspots:', err);
    }
    setHotspotsLoading(false);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save hotspots to database
  const handleHotspotsChange = async (newHotspots) => {
    setHotspots(newHotspots);
    
    if (!post.id) return; // Can only save hotspots for existing posts
    
    try {
      // Delete existing hotspots
      const deleteResult = await supabase
        .from('blueprint_post_hotspots')
        .delete()
        .eq('post_id', post.id);
      
      if (deleteResult.error) {
        console.error('Error deleting hotspots:', deleteResult.error);
        alert('Errore eliminazione hotspots: ' + (deleteResult.error.message || JSON.stringify(deleteResult.error)));
        return;
      }
      
      // Insert new hotspots
      if (newHotspots.length > 0) {
        const hotspotsToInsert = newHotspots.map(h => ({
          post_id: post.id,
          position_x: h.position_x,
          position_y: h.position_y,
          label: h.label,
          link_type: h.link_type || 'material',
          material_id: h.material_id,
          link_url: h.link_url
        }));
        
        const insertResult = await supabase
          .from('blueprint_post_hotspots')
          .insert(hotspotsToInsert);
        
        if (insertResult.error) {
          console.error('Error inserting hotspots:', insertResult.error);
          alert('Errore salvataggio hotspots: ' + (insertResult.error.message || JSON.stringify(insertResult.error)));
        }
      }
    } catch (err) {
      console.error('Error saving hotspots:', err);
      alert('Errore: ' + (err.message || String(err)));
    }
  };

  // Dynamically import components to avoid bundle issues
  const [TipTapEditor, setTipTapEditor] = useState(null);
  const [HotspotEditor, setHotspotEditor] = useState(null);
  const [SectionsEditor, setSectionsEditor] = useState(null);
  const [MaterialsEditor, setMaterialsEditor] = useState(null);
  const [GalleryEditor, setGalleryEditor] = useState(null);

  useEffect(() => {
    import('../components/blueprint/TipTapEditor').then(mod => setTipTapEditor(() => mod.default)).catch(() => {});
    import('../components/blueprint/ImageHotspotEditor').then(mod => setHotspotEditor(() => mod.default)).catch(() => {});
    import('../components/blueprint/PostSectionsEditor').then(mod => setSectionsEditor(() => mod.default)).catch(() => {});
    import('../components/blueprint/PostMaterialsEditor').then(mod => setMaterialsEditor(() => mod.default)).catch(() => {});
    import('../components/blueprint/PostGalleryEditor').then(mod => setGalleryEditor(() => mod.default)).catch(() => {});
  }, []);

  // Load sections, materials and gallery for existing posts
  const [postSections, setPostSections] = useState([]);
  const [postMaterials, setPostMaterials] = useState([]);
  const [postGallery, setPostGallery] = useState([]);

  useEffect(() => {
    if (post.id) {
      // Load sections
      supabase
        .from('blueprint_post_sections')
        .select('*')
        .eq('post_id', post.id)
        .order('order_index')
        .then(({ data }) => { if (data) setPostSections(data); });
      
      // Load post materials
      supabase
        .from('blueprint_post_materials')
        .select('*, material:materials(id, name, image_url, category, origin)')
        .eq('post_id', post.id)
        .order('order_index')
        .then(({ data }) => { 
          if (data) setPostMaterials(data.map(pm => ({ ...pm.material, note: pm.note, order_index: pm.order_index }))); 
        });
      
      // Load gallery
      supabase
        .from('blueprint_post_gallery')
        .select('*')
        .eq('post_id', post.id)
        .order('order_index')
        .then(async ({ data }) => { 
          if (data && data.length > 0) {
            // Load gallery hotspots
            const galleryIds = data.map(g => g.id);
            const { data: hotspotsData } = await supabase
              .from('blueprint_gallery_hotspots')
              .select('*, material:materials(id, name, image_url, category)')
              .in('gallery_image_id', galleryIds);
            
            const hotspotsMap = {};
            if (hotspotsData) {
              hotspotsData.forEach(h => {
                if (!hotspotsMap[h.gallery_image_id]) hotspotsMap[h.gallery_image_id] = [];
                hotspotsMap[h.gallery_image_id].push(h);
              });
            }
            
            setPostGallery(data.map(g => ({ ...g, hotspots: hotspotsMap[g.id] || [] })));
          }
        });
    }
  }, [post.id]);

  // Save sections
  const handleSectionsChange = async (newSections) => {
    setPostSections(newSections);
    
    if (!post.id) return;
    
    try {
      // Delete existing sections
      await supabase.from('blueprint_post_sections').delete().eq('post_id', post.id);
      
      // Insert new sections
      if (newSections.length > 0) {
        const sectionsToInsert = newSections.map((s, idx) => ({
          post_id: post.id,
          order_index: idx,
          title_it: s.title_it,
          content_it: s.content_it,
          image_url: s.image_url,
          image_position: s.image_position || 'none',
          image_width: s.image_width || 50,
          video_url: s.video_url,
          video_thumbnail_url: s.video_thumbnail_url,
          video_duration: s.video_duration,
          video_title: s.video_title,
          video_description: s.video_description,
          anchor_id: s.anchor_id || `section-${idx}`,
          is_visible: s.is_visible !== false
        }));
        
        await supabase.from('blueprint_post_sections').insert(sectionsToInsert);
      }
    } catch (err) {
      console.error('Error saving sections:', err);
    }
  };

  // Save post materials
  const handleMaterialsChange = async (newMaterials) => {
    setPostMaterials(newMaterials);
    
    if (!post.id) return;
    
    try {
      // Delete existing post materials
      const deleteResult = await supabase
        .from('blueprint_post_materials')
        .delete()
        .eq('post_id', post.id);
      
      if (deleteResult.error) {
        console.error('Error deleting materials:', deleteResult.error);
        alert('Errore eliminazione materiali: ' + (deleteResult.error.message || JSON.stringify(deleteResult.error)));
        return;
      }
      
      // Insert new materials
      if (newMaterials.length > 0) {
        const materialsToInsert = newMaterials.map((m, idx) => ({
          post_id: post.id,
          material_id: m.id,
          order_index: idx,
          note: m.note || null
        }));
        
        const insertResult = await supabase
          .from('blueprint_post_materials')
          .insert(materialsToInsert);
        
        if (insertResult.error) {
          console.error('Error inserting materials:', insertResult.error);
          alert('Errore salvataggio materiali: ' + (insertResult.error.message || JSON.stringify(insertResult.error)));
        }
      }
    } catch (err) {
      console.error('Error saving materials:', err);
      alert('Errore: ' + (err.message || String(err)));
    }
  };

  // Save gallery
  const handleGalleryChange = async (newGallery) => {
    setPostGallery(newGallery);
    
    if (!post.id) return;
    
    try {
      // Delete existing gallery
      await supabase.from('blueprint_post_gallery').delete().eq('post_id', post.id);
      
      // Insert new gallery images
      if (newGallery.length > 0) {
        const galleryToInsert = newGallery.map((img, idx) => ({
          post_id: post.id,
          order_index: idx,
          image_url: img.image_url,
          thumbnail_url: img.thumbnail_url,
          alt_text: img.alt_text,
          caption_it: img.caption_it,
          width: img.width,
          height: img.height,
          is_visible: img.is_visible !== false
        }));
        
        const { data: insertedGallery } = await supabase
          .from('blueprint_post_gallery')
          .insert(galleryToInsert)
          .select();
        
        // Save hotspots for each gallery image
        if (insertedGallery) {
          for (let i = 0; i < insertedGallery.length; i++) {
            const galleryImage = insertedGallery[i];
            const originalImage = newGallery[i];
            
            if (originalImage.hotspots?.length > 0) {
              const hotspotsToInsert = originalImage.hotspots.map(h => ({
                gallery_image_id: galleryImage.id,
                position_x: h.position_x,
                position_y: h.position_y,
                label: h.label,
                link_type: h.link_type || 'material',
                material_id: h.material_id,
                link_url: h.link_url
              }));
              
              await supabase
                .from('blueprint_gallery_hotspots')
                .insert(hotspotsToInsert);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error saving gallery:', err);
    }
  };

  return (
    <div className="bg-[#1a1a18] rounded-xl border border-white/5 p-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-white">
          {post.id ? 'Modifica Articolo' : 'Nuovo Articolo'}
        </h3>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onSave(formData)}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#D6C1A5] text-black rounded-lg text-sm font-bold hover:bg-[#c4af93] transition-colors shadow-lg"
          >
            <Save size={18} />
            Salva Articolo
          </button>
          <button onClick={onCancel} className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-4">
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'content' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          Contenuto
        </button>
        <button
          onClick={() => setActiveTab('sections')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'sections' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          Sezioni
          {postSections.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
              {postSections.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('materials')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'materials' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          Materiali
          {postMaterials.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
              {postMaterials.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('hotspots')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'hotspots' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          Cover Hotspots
          {hotspots.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
              {hotspots.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('gallery')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'gallery' 
              ? 'bg-[#D6C1A5] text-[#0a0a09]' 
              : 'bg-[#2a2a28] text-white/60 hover:text-white'
          }`}
        >
          Gallery
          {postGallery.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
              {postGallery.length}
            </span>
          )}
        </button>
      </div>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="text-sm text-white/60 block mb-1">Titolo *</label>
              <input
                type="text"
                value={formData.title_it || ''}
                onChange={(e) => updateField('title_it', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Titolo dell'articolo"
              />
            </div>

            <div>
              <label className="text-sm text-white/60 block mb-1">Slug</label>
              <input
                type="text"
                value={formData.slug || ''}
                onChange={(e) => updateField('slug', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="url-articolo"
              />
            </div>

            <div>
              <label className="text-sm text-white/60 block mb-1">Sottotitolo</label>
              <input
                type="text"
                value={formData.subtitle_it || ''}
                onChange={(e) => updateField('subtitle_it', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Breve sottotitolo"
              />
            </div>

            <div>
              <label className="text-sm text-white/60 block mb-1">Excerpt (descrizione breve)</label>
              <textarea
                value={formData.excerpt_it || ''}
                onChange={(e) => updateField('excerpt_it', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                placeholder="Breve descrizione per le card..."
              />
            </div>

            <div>
              <label className="text-sm text-white/60 block mb-1">Contenuto</label>
              {TipTapEditor ? (
                <TipTapEditor
                  content={formData.content_it || ''}
                  onChange={(html) => updateField('content_it', html)}
                  placeholder="Scrivi il contenuto dell'articolo..."
                />
              ) : (
                <textarea
                  value={formData.content_it || ''}
                  onChange={(e) => updateField('content_it', e.target.value)}
                  rows={10}
                  className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none font-mono text-sm"
                  placeholder="Contenuto dell'articolo..."
                />
              )}
            </div>
          </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/60 block mb-1">Immagine di copertina</label>
            <input
              type="text"
              value={formData.cover_image_url || ''}
              onChange={(e) => updateField('cover_image_url', e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none text-sm"
              placeholder="https://..."
            />
            {formData.cover_image_url && (
              <img src={formData.cover_image_url} alt="" className="mt-2 w-full h-32 object-cover rounded-lg" />
            )}
          </div>

          <div>
            <label className="text-sm text-white/60 block mb-1">Categoria</label>
            <select
              value={formData.category_id || ''}
              onChange={(e) => updateField('category_id', e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
            >
              <option value="">Seleziona categoria</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name_it || cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-white/60 block mb-1">Autore</label>
            <select
              value={formData.author_id || ''}
              onChange={(e) => updateField('author_id', e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
            >
              <option value="">Seleziona autore</option>
              {authors.map(auth => (
                <option key={auth.id} value={auth.id}>{auth.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-white/60 block mb-1">Tempo di lettura (minuti)</label>
            <input
              type="number"
              value={formData.reading_time || 5}
              onChange={(e) => updateField('reading_time', parseInt(e.target.value))}
              className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
              min={1}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_featured"
              checked={formData.is_featured || false}
              onChange={(e) => updateField('is_featured', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <label htmlFor="is_featured" className="text-sm text-white/60">In evidenza</label>
          </div>
        </div>
      </div>
      )}

      {/* Hotspots Tab */}
      {activeTab === 'hotspots' && (
        <div>
          {!post.id ? (
            <div className="text-center py-12 text-white/40">
              <p className="mb-2">Salva prima l'articolo per aggiungere gli hotspots</p>
              <button
                onClick={() => setActiveTab('content')}
                className="text-[#D6C1A5] hover:underline"
              >
                Torna al contenuto
              </button>
            </div>
          ) : !formData.cover_image_url ? (
            <div className="text-center py-12 text-white/40">
              <p className="mb-2">Aggiungi un'immagine di copertina per creare hotspots</p>
              <button
                onClick={() => setActiveTab('content')}
                className="text-[#D6C1A5] hover:underline"
              >
                Torna al contenuto
              </button>
            </div>
          ) : HotspotEditor ? (
            <HotspotEditor
              imageUrl={formData.cover_image_url}
              hotspots={hotspots}
              onHotspotsChange={handleHotspotsChange}
              postId={post.id}
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#D6C1A5]" />
            </div>
          )}
        </div>
      )}

      {/* Sections Tab */}
      {activeTab === 'sections' && (
        <div>
          {!post.id ? (
            <div className="text-center py-12 text-white/40">
              <p className="mb-2">Salva prima l'articolo per aggiungere le sezioni</p>
              <button
                onClick={() => setActiveTab('content')}
                className="text-[#D6C1A5] hover:underline"
              >
                Torna al contenuto
              </button>
            </div>
          ) : SectionsEditor ? (
            <SectionsEditor
              postId={post.id}
              sections={postSections}
              onChange={handleSectionsChange}
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#D6C1A5]" />
            </div>
          )}
        </div>
      )}

      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <div>
          {!post.id ? (
            <div className="text-center py-12 text-white/40">
              <p className="mb-2">Salva prima l'articolo per aggiungere i materiali</p>
              <button
                onClick={() => setActiveTab('content')}
                className="text-[#D6C1A5] hover:underline"
              >
                Torna al contenuto
              </button>
            </div>
          ) : MaterialsEditor ? (
            <MaterialsEditor
              postId={post.id}
              materials={postMaterials}
              onChange={handleMaterialsChange}
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#D6C1A5]" />
            </div>
          )}
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <div>
          {!post.id ? (
            <div className="text-center py-12 text-white/40">
              <p className="mb-2">Salva prima l'articolo per aggiungere la gallery</p>
              <button
                onClick={() => setActiveTab('content')}
                className="text-[#D6C1A5] hover:underline"
              >
                Torna al contenuto
              </button>
            </div>
          ) : GalleryEditor ? (
            <GalleryEditor
              postId={post.id}
              gallery={postGallery}
              onChange={handleGalleryChange}
            />
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#D6C1A5]" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MATERIALS SECTION - Gestione materiali (collegato a DB materials)
// ============================================================================

const MaterialsSection = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    loadMaterials();
  }, [page, filterCategory, searchTerm]);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('materials')
        .select('*', { count: 'exact' });

      if (filterCategory) {
        query = query.eq('category', filterCategory);
      }

      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }

      const from = (page - 1) * ITEMS_PER_PAGE;
      query = query.range(from, from + ITEMS_PER_PAGE - 1).order('name');

      const { data, count, error } = await query;
      
      if (error) throw error;
      setMaterials(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error loading materials:', err);
    }
    setLoading(false);
  };

  const updateMaterial = async (materialData) => {
    const { error } = await supabase
      .from('materials')
      .update(materialData)
      .eq('id', materialData.id);
    
    if (!error) {
      setEditingMaterial(null);
      loadMaterials();
    } else {
      alert('Errore: ' + error.message);
    }
  };

  const deleteMaterial = async (materialId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo materiale?')) return;
    
    const { error } = await supabase.from('materials').delete().eq('id', materialId);
    if (!error) {
      loadMaterials();
    }
  };

  const categories = ['marble', 'quartzite', 'granite', 'onyx', 'travertine', 'limestone'];
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light flex items-center gap-3">
            <Database className="w-7 h-7 text-[#D6C1A5]" />
            Gestione Materiali
          </h2>
          <p className="text-white/50 text-sm mt-1">
            {totalCount} materiali nel database
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder="Cerca materiali..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#1a1a18] border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#D6C1A5] focus:outline-none"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
          className="px-4 py-2 rounded-lg bg-[#1a1a18] border border-white/10 text-white text-sm focus:border-[#D6C1A5] focus:outline-none"
        >
          <option value="">Tutte le categorie</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Materials Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#D6C1A5]" />
        </div>
      ) : (
        <div className="bg-[#1a1a18] rounded-xl border border-white/5 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0a0a09]">
              <tr>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Materiale</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Categoria</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Origine</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">DNA</th>
                <th className="text-right px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {materials.map(mat => (
                <tr key={mat.id} className="hover:bg-white/5">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {mat.image_url ? (
                        <img src={mat.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#2a2a28] flex items-center justify-center">
                          <Layers size={16} className="text-white/20" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium text-sm">{mat.name}</p>
                        <p className="text-white/40 text-xs">{mat.id.substring(0, 8)}...</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D6C1A5]/20 text-[#D6C1A5] capitalize">
                      {mat.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white/60 text-sm">{mat.origin || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${mat.has_dna ? 'text-green-400' : 'text-white/30'}`}>
                      {mat.has_dna ? '✓ Analizzato' : 'Non analizzato'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => setEditingMaterial(mat)}
                        className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
                      >
                        <Edit size={16} />
                      </button>
                      <a 
                        href={`/materials/${mat.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button 
                        onClick={() => deleteMaterial(mat.id)}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-white/40 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
              <span className="text-white/40 text-sm">
                Pagina {page} di {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded-lg bg-[#2a2a28] text-white/60 text-sm disabled:opacity-30"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 rounded-lg bg-[#2a2a28] text-white/60 text-sm disabled:opacity-30"
                >
                  Successiva
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Material Modal */}
      {editingMaterial && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a18] rounded-xl border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-white">Modifica Materiale</h3>
                <button onClick={() => setEditingMaterial(null)} className="text-white/40 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-white/60 block mb-1">Nome</label>
                  <input
                    type="text"
                    value={editingMaterial.name || ''}
                    onChange={(e) => setEditingMaterial(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/60 block mb-1">Categoria</label>
                    <select
                      value={editingMaterial.category || ''}
                      onChange={(e) => setEditingMaterial(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-white/60 block mb-1">Origine</label>
                    <input
                      type="text"
                      value={editingMaterial.origin || ''}
                      onChange={(e) => setEditingMaterial(prev => ({ ...prev, origin: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-white/60 block mb-1">URL Immagine</label>
                  <input
                    type="text"
                    value={editingMaterial.image_url || ''}
                    onChange={(e) => setEditingMaterial(prev => ({ ...prev, image_url: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg bg-[#0a0a09] border border-white/10 text-white focus:border-[#D6C1A5] focus:outline-none"
                  />
                  {editingMaterial.image_url && (
                    <img src={editingMaterial.image_url} alt="" className="mt-2 w-full h-40 object-cover rounded-lg" />
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => updateMaterial(editingMaterial)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#D6C1A5] text-black rounded-lg text-sm font-medium hover:bg-[#c4af93]"
                  >
                    <Save size={16} />
                    Salva
                  </button>
                  <button
                    onClick={() => setEditingMaterial(null)}
                    className="px-4 py-2 border border-white/10 text-white/60 rounded-lg text-sm hover:bg-white/5"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueprintAdminPage;
